import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import AdminPageHeader from "../../components/admin/AdminPageHeader";
import { createPortal } from "react-dom";
import { parseInstant } from "../../utils/dateUtils";
import { adminService } from "../../features/admin/service";
import { parseApiError } from "../../lib/http/apiErrors";
import type { AdminPromptDbOverride, AdminPromptEntry, AdminPromptsResponse } from "../../features/admin/types";
import Spinner from "../../components/ui/Spinner";
import HelpKbSection from "./HelpKbSection";
import RefreshButton from "../../components/admin/RefreshButton";
import { useAdminToast } from "../../context/AdminToastContext";

// ── Pipeline definitions ──────────────────────────────────────────────────────
// Each pipeline maps to the actual runtime execution order.
// promptId references the registry IDs from the backend.
// type "info" = non-LLM step (Pinecone, FAL, etc.) shown as informational card.

type PipelineStep =
  // roleGate: this step ALWAYS runs — never skipped, never truly "optional" — but which one
  // of a pair runs is decided per-message by session.personalization_profile.is_seller.
  // Exactly one of a "buyer" / "seller" pair fires for every message; never both, never neither.
  | { type?: "prompt"; promptId: string; label: string; note?: string; optional?: boolean; roleGate?: "buyer" | "seller" }
  // promptId is optional here: an "info" step is never Test/Run-able (no text
  // completion makes sense for an image model), but when it names a real
  // PROMPT_REGISTRY entry (e.g. an image-gen model chain), the admin can still
  // find it in the prompt list to set/publish a model override.
  | { type: "info"; label: string; note: string; icon?: string; promptId?: string };

// What happens when admin uploads a file for this pipeline
interface UploadAutoFill {
  // For image uploads: which promptId gets the data-URL in imageUrls
  imageTarget?: string[];
  // For PDF/DOCX uploads: which promptId + varName gets the extracted text
  documentTarget?: { promptId: string; varName: string };
  // For video uploads: which promptId(s) get frame data-URLs as imageUrls
  videoTarget?: string[];
}

interface PipelineUploadConfig {
  accepts: string;         // input accept attribute
  label: string;           // shown in dropzone
  hint: string;
  autoFill: UploadAutoFill;
}

interface Pipeline {
  id: string;
  groupId: string;
  title: string;
  description: string;
  steps: PipelineStep[];
  uploadConfig?: PipelineUploadConfig;
}

const PIPELINES: Pipeline[] = [
  // ── Deal Analysis ────────────────────────────────────────────────────────────
  {
    id: "deal_analysis_main",
    groupId: "deal_analysis",
    title: "Deal Analysis — Full Flow",
    description:
      "Seller uploads a deal (image / PDF / DOCX / video) → LLM extracts structured data → Pinecone vector indexing so buyers can discover it via RAG chat.",
    uploadConfig: {
      accepts: "image/*,application/pdf,.pdf,.docx,.doc,video/*",
      label: "Upload deal asset to test",
      hint: "Flyer image, PDF/DOCX brochure, or video — mirrors what a seller uploads",
      autoFill: {
        imageTarget: ["deal_analysis_extract"],
        documentTarget: { promptId: "deal_analysis_extract", varName: "document_text" },
        videoTarget: ["deal_analysis_extract", "deal_analysis_video_addendum"],
      },
    },
    steps: [
      {
        promptId: "deal_analysis_extract",
        label: "Step 1 — Structured Extraction",
        note: "Core LLM call. Runs for every deal. Produces suggested_title, categories, destination, pricing, itinerary, tags, enriched_summary.",
      },
      {
        promptId: "deal_analysis_video_addendum",
        label: "Step 1b — Video Addendum",
        optional: true,
        note: "Appended to Step 1 only when the deal asset is a video. Tells the model to treat images as sequential video frames.",
      },
      {
        type: "info",
        icon: "🔷",
        label: "Step 2 — Pinecone Vector Indexing",
        note: "Not an LLM prompt — happens automatically after extraction. The enriched_summary + deal metadata are embedded and upserted into Pinecone under the deal's category namespace (e.g. family_trips, hotels, cruises). This is what makes the deal searchable in RAG v2 chat.",
      },
    ],
  },

  // ── Deal Auto-Flyer ──────────────────────────────────────────────────────────
  {
    id: "deal_auto_flyer",
    groupId: "deal_analysis",
    title: "Deal Auto-Flyer",
    description:
      "Fired as a background asyncio task (extraction_agent.py:492, asyncio.create_task) from inside the SAME deal-analysis job right after typed-collection promotion — " +
      "no separate trigger, no scheduled job, no worker process. It runs CONCURRENTLY with Pinecone indexing/buyer notifications below it, not strictly before or after " +
      "them — the deal can finish indexing while its flyer is still generating (tracked separately via the deal's flyer_auto_status field: running/done/failed). " +
      "When a seller creates a deal via image/PDF/DOC/DOCX upload, this builds a marketing flyer from the extracted content. Distinct from the manual " +
      "\"Flyer — Document Upload\" AI Media tool: same two text-prompt steps, but its own dedicated 2-model image chain.",
    steps: [
      {
        promptId: "flyer_from_doc_extract",
        label: "Step 1 — Extract Structured Details + Visual Theme",
        note: "Same extraction prompt the manual Document Upload flyer tool uses — reads the deal's OCR'd/extracted text and derives product_name, headline, key_specs, pricing, and a visual theme.",
      },
      {
        promptId: "flyer_from_doc_prompt_synth",
        label: "Step 2 — Synthesize Final Image Generation Prompt",
        note: "Takes the structured extraction and writes the single dense FAL image-generation prompt for the flyer.",
      },
      {
        type: "info",
        icon: "🖼",
        promptId: "deal_auto_flyer_image_gen",
        label: "Step 3 — Image Generation Model",
        note: "Not a text LLM prompt. Own chain, separate from the shared flyer studio chain above: Grok → Gemini 2.5 Flash Image. Admin-overridable — find \"Deal Auto-Flyer — Image Generation Model\" in the prompt list to set/publish an override (tried first; the coded chain remains the fallback if it fails).",
      },
    ],
  },

  // ── Deal Analysis — Seller Profile Summary ────────────────────────────────────
  {
    id: "seller_profile_summary",
    groupId: "deal_analysis",
    title: "Seller Profile Summary",
    description:
      "Separate from deal-image analysis above — generates a 3-5 sentence third-person profile paragraph from the seller's OWN profile fields (name, agency, location, seller_types, bio, destinations/segments). Stored on the user and pushed to every one of their deals so RAG search carries seller context without a separate lookup at query time.",
    steps: [
      {
        promptId: "seller_summary",
        label: "Step 1 — Generate Seller Profile Paragraph",
        note: "Runs when a seller updates their profile, or an admin triggers a manual refresh via /api/admin/seller-summary/refresh/{user_id}.",
      },
    ],
  },

  // ── AI Media — Flyer (Template) ─────────────────────────────────────────────
  {
    id: "ai_media_flyer_template",
    groupId: "ai_media",
    title: "Flyer — Template",
    uploadConfig: {
      accepts: "image/*",
      label: "Upload seller reference photos (optional)",
      hint: "Destination photos, existing flyer, or logo — mirrors what a seller attaches mid-chat",
      autoFill: { imageTarget: ["template_reference_describe_images"] },
    },
    description:
      "Seller picks a flyer template and chats to fill fields. Each message is parsed for field values; the bot asks follow-up questions until all required fields are collected, then builds the final FAL/Flux image-generation prompt.",
    steps: [
      {
        promptId: "template_reference_describe_images",
        label: "Step 1 — Describe Uploaded Reference Photos",
        optional: true,
        note: "Runs only if the seller attaches images mid-chat. Summarises each photo so the bot can reference them in follow-up questions and field parsing.",
      },
      {
        promptId: "ai_media_template_parse",
        label: "Step 2 — Parse User Message → Template Fields",
        note: "Every seller message runs through here. Extracts field values (destination, price, duration, hotels…) AND visual style overrides (mood, color_tone, layout) in one pass.",
      },
      {
        promptId: "ai_media_followup_flyer",
        label: "Step 3 — Follow-up Question (missing fields)",
        optional: true,
        note: "Generated when required fields are still missing. Leads with the missing required fields then suggests optional extras relevant to the deal category.",
      },
      {
        promptId: "template_intent_classifier",
        label: "Step 4 — Classify Free-Text Reply Intent",
        optional: true,
        note: "Runs only when the seller's reply is free text (not a chip tap) during any of the follow-ups below — decides which one it's actually answering (logo, pricing, address, phone).",
      },
      {
        promptId: "template_logo_followup",
        label: "Step 5 — Follow-up: Add Agency Logo?",
        optional: true,
        note: "Shown once per session when the seller hasn't decided on a logo yet.",
      },
      {
        promptId: "template_pricing_confirmation",
        label: "Step 6 — Follow-up: Pricing Format (1 of 2)",
        optional: true,
        note: "Asks how pricing should be shown (per person / total / per room / per cabin), whenever pricing hasn't been asked about yet this session.",
      },
      {
        promptId: "template_pricing_value_followup",
        label: "Step 7 — Follow-up: Actual Price (2 of 2)",
        optional: true,
        note: "Immediately after the seller picks a pricing format chip in Step 6 — asks for the actual figure, with format-specific chip suggestions.",
      },
      {
        promptId: "template_contact_confirmation",
        label: "Step 8 — Follow-up: Confirm Phone/Address for Footer",
        optional: true,
        note: "Confirms (or asks for) the seller's contact info before it's placed in the flyer footer.",
      },
      {
        promptId: "template_address_followup",
        label: "Step 9 — Follow-up: Address to Show on Flyer",
        optional: true,
        note: "Edit-session address updates — offers the profile address or lets the seller skip.",
      },
      {
        promptId: "template_styling_followup_flyer",
        label: "Step 10 — Follow-up: Visual Style Picker",
        optional: true,
        note: "Asked before final generation so the seller can pick or override the visual vibe (luxury, vibrant, minimal, dark…).",
      },
      {
        promptId: "template_brief_ready",
        label: "Step 11 — Review Screen (Brief Ready)",
        note: "Pre-generation recap once styling + contact + pricing follow-ups are resolved — seller reviews and taps Generate.",
      },
      {
        promptId: "ai_media_image_prompt_polish",
        label: "Step 12 — Polish Final Image Generation Prompt",
        note: "Once all required fields are collected. Takes the structured context and writes the dense single-paragraph prompt sent to FAL/Flux to render the final flyer.",
      },
      {
        type: "info",
        icon: "🖼",
        promptId: "flyer_studio_image_gen",
        label: "Step 13 — Image Generation Model",
        note: "Not a text LLM prompt. Model chain: Grok → IMAGE_MODEL_FLYER → Gemini 2.5 Flash Image. Admin-overridable — find \"Flyer Studio — Image Generation Model\" in the prompt list to set/publish an override (tried first; coded chain remains the fallback).",
      },
    ],
  },

  // AI Media — Video (Template) pipeline removed from the playground for now
  // (kept in PROMPT_REGISTRY/backend — ai_media_followup_video_desc and
  // ai_media_video_prompt_polish still back the live video-reel chat flow and
  // are still reachable/editable via the Itinerary pipeline's own Step 2b,
  // which reuses ai_media_video_prompt_polish for its optional reel prompt).
  // Re-add a "AI Media — Video (Template)" entry here (mirroring the Flyer
  // block above, swap image_prompt_polish → video_prompt_polish) to bring it
  // back once video generation is ready to be surfaced again.

  // ── AI Media — Itinerary (Template) ─────────────────────────────────────────
  {
    id: "ai_media_itinerary_template",
    groupId: "ai_media",
    title: "Itinerary — Template",
    description:
      "Seller chats to fill itinerary template fields (destination, days, hotels, inclusions, exclusions, pricing tiers). Once complete, generates a full structured itinerary JSON with day-wise breakdown, stays, and cover image queries.",
    steps: [
      {
        promptId: "ai_media_template_parse",
        label: "Step 1 — Parse User Message → Itinerary Fields",
        note: "Extracts itinerary-specific fields: destination, duration, departure city, hotels, inclusions, exclusions, group size, pricing tiers.",
      },
      {
        promptId: "ai_media_followup_itinerary",
        label: "Step 2 — Follow-up: Collect Missing Itinerary Details",
        optional: true,
        note: "Warm B2B-agent tone follow-up. Asks for missing details with category-relevant extras (e.g. cabin type for cruises, venue/guest-count for events).",
      },
      {
        promptId: "template_intent_classifier",
        label: "Step 3 — Classify Free-Text Reply Intent",
        optional: true,
        note: "Runs only when the seller's reply is free text (not a chip tap) during any of the follow-ups below — decides which one it's actually answering (pricing, address, phone).",
      },
      {
        promptId: "template_pricing_confirmation",
        label: "Step 4 — Follow-up: Pricing Format (1 of 2)",
        optional: true,
        note: "Asks how pricing should be shown (per person / total / per room / per cabin), whenever pricing hasn't been asked about yet this session.",
      },
      {
        promptId: "template_pricing_value_followup",
        label: "Step 5 — Follow-up: Actual Price (2 of 2)",
        optional: true,
        note: "Immediately after the seller picks a pricing format chip in Step 4 — asks for the actual figure, with format-specific chip suggestions.",
      },
      {
        promptId: "template_contact_confirmation",
        label: "Step 6 — Follow-up: Confirm Phone/Address for Footer",
        optional: true,
        note: "Confirms (or asks for) the seller's contact info before it's placed in the itinerary footer.",
      },
      {
        promptId: "template_address_followup",
        label: "Step 7 — Follow-up: Address to Show",
        optional: true,
        note: "Edit-session address updates — offers the profile address or lets the seller skip.",
      },
      {
        promptId: "template_styling_followup_itinerary",
        label: "Step 8 — Follow-up: Layout Style Picker",
        optional: true,
        note: "Itinerary-specific version of the styling follow-up (classic, modern, vibrant, dark…) — different chip set than the flyer version.",
      },
      {
        promptId: "template_brief_ready",
        label: "Step 9 — Review Screen (Brief Ready)",
        note: "Pre-generation recap once styling + contact + pricing follow-ups are resolved — seller reviews and taps Generate.",
      },
      {
        promptId: "itinerary_generation_system",
        label: "Step 10 — Generate Structured Itinerary JSON",
        note: "Once all required fields are collected. Produces the full itinerary JSON: coverImage, tripTitle, pricingTiers, stays, inclusions, exclusions, dayWiseSummary, detailedItinerary.",
      },
      {
        type: "info",
        icon: "🖼",
        promptId: "itinerary_image_gen",
        label: "Step 11 — Cover / Day Image Generation Model",
        note: "Not a text LLM prompt. Model chain: Grok → Gemini-pro → Gemini 2.5 Flash Image. Admin-overridable — find \"Itinerary — Image Generation Model\" in the prompt list to set/publish an override (tried first; coded chain remains the fallback).",
      },
      {
        type: "info",
        icon: "📄",
        label: "Step 12 — Render HTML / PDF",
        note: "Not an LLM prompt — the itinerary JSON is rendered using the selected template's HTML/CSS layout and exported as a PDF or DOCX for the seller.",
      },
    ],
  },

  // Free-form flyer generation removed from the playground: its backend
  // route (POST /rag/generate-media — the non-streaming one, using
  // extract_context/ai_media_context_extract) has no live caller anywhere in
  // frontend/ or web-frontend/ — only POST /rag/generate-media/stream is
  // ever called, and that route is exclusively the flyer-EDIT-with-an-
  // uploaded-flyer flow (see stream_flyer_edit_media), already covered by
  // the "Flyer — Edit Existing" pipeline below. There is no reachable
  // "type a request, no template, no upload" flyer path in the current app.
  // ai_media_context_extract/ai_media_image_prompt_polish still exist as
  // testable prompts in the Generation Playground tab if that dead route is
  // ever revived.

  // ── AI Media — Doc → Flyer ──────────────────────────────────────────────────
  {
    id: "ai_media_doc_to_flyer",
    groupId: "ai_media",
    title: "Flyer — Document Upload",
    uploadConfig: {
      accepts: "application/pdf,.pdf,.docx,.doc",
      label: "Upload product brochure (PDF or DOCX)",
      hint: "Hotel spec sheet, private-jet brochure, package PDF — text is extracted and auto-fills the raw_text variable in Step 2",
      autoFill: { documentTarget: { promptId: "flyer_from_doc_extract", varName: "raw_text" } },
    },
    description:
      "Seller uploads a PDF/DOCX brochure (hotel, private jet, package, cruise). The document is OCR'd and passed through two LLM steps to produce the final marketing flyer without needing a template.",
    steps: [
      {
        type: "info",
        icon: "📑",
        label: "Step 1 — OCR / Text Extraction",
        note: "Not an LLM prompt — the uploaded PDF/DOCX is processed via PyMuPDF / docx2txt to extract raw text (sent in full to Step 2, no truncation).",
      },
      {
        promptId: "flyer_from_doc_extract",
        label: "Step 2 — Extract Structured Details + Visual Theme",
        note: "Reads the raw OCR text and extracts product_name, headline, subheadline, tagline, key_specs, features, inclusions, pricing, contact, and infers a visual theme (color palette, mood, style, background).",
      },
      {
        promptId: "flyer_from_doc_prompt_synth",
        label: "Step 3 — Synthesize Final Image Generation Prompt",
        note: "Takes the structured extraction output and writes the single dense FAL image-generation prompt that renders the finished marketing flyer.",
      },
      {
        type: "info",
        icon: "🖼",
        promptId: "flyer_studio_image_gen",
        label: "Step 4 — Image Generation Model",
        note: "Not a text LLM prompt. Same shared chain as every other manual flyer flow: Grok → IMAGE_MODEL_FLYER → Gemini 2.5 Flash Image. Admin-overridable — find \"Flyer Studio — Image Generation Model\" in the prompt list.",
      },
    ],
  },

  // ── AI Media — Edit Flyer ────────────────────────────────────────────────────
  {
    id: "ai_media_edit_flyer",
    groupId: "ai_media",
    title: "Flyer — Edit Existing",
    uploadConfig: {
      accepts: "image/*",
      label: "Upload the existing flyer to edit",
      hint: "The flyer image the seller wants to modify — auto-fills Step 1 (OCR vision prompt)",
      autoFill: { imageTarget: ["flyer_edit_ocr"] },
    },
    description:
      "Seller uploads an existing flyer image and asks for changes (update price, add phone, change headline, etc.). Three LLM steps: read what's on the flyer → parse what to change → build a surgical edit instruction for the image model.",
    steps: [
      {
        promptId: "flyer_edit_ocr",
        label: "Step 1 — OCR Existing Flyer",
        note: "Vision pass that reads every visible text/layout element off the uploaded flyer image into structured JSON (destination, headline, price, agency, contact, style).",
      },
      {
        promptId: "flyer_edit_parse_instructions",
        label: "Step 2 — Parse Edit Instructions",
        note: "Given the OCR'd flyer content and the seller's edit request, decides what to keep vs. change vs. add — including brand-new fields not on the original flyer.",
      },
      {
        promptId: "flyer_edit_build_prompt",
        label: "Step 3 — Build Surgical Edit Instruction",
        note: "Converts the parsed edit spec into a minimal instruction for the AI image editor — describing ONLY the additions/changes, so the rest of the flyer is preserved pixel-perfect.",
      },
      {
        type: "info",
        icon: "✏️",
        label: "Step 4 — FAL Image Edit Model",
        note: "Not an LLM prompt — the surgical edit instruction is sent to the image-edit model (FAL flux-kontext or similar) which applies only the requested changes to the original flyer.",
      },
    ],
  },

  // ── AI Media — Edit Itinerary (upload) ─────────────────────────────────────
  {
    id: "ai_media_edit_itinerary",
    groupId: "ai_media",
    title: "Itinerary — Upload & Edit",
    uploadConfig: {
      accepts: "image/*,application/pdf,.pdf,.docx,.doc",
      label: "Upload itinerary pages (images, PDF, or DOCX)",
      hint: "Images → auto-fills Step 1 (vision extract). PDF/DOCX → auto-fills extracted_text in Step 2 (parse content)",
      autoFill: {
        imageTarget: ["itinerary_edit_vision_extract"],
        documentTarget: { promptId: "itinerary_edit_parse_content", varName: "extracted_text" },
      },
    },
    description:
      "Seller uploads existing itinerary pages (PDF / DOCX / images) to import and then edit. Three steps: extract text → parse into itinerary JSON → handle edit requests.",
    steps: [
      {
        promptId: "itinerary_edit_vision_extract",
        label: "Step 1 — Extract Text from Image Pages (vision)",
        optional: true,
        note: "Only when the seller uploads itinerary as image files. Transcribes every word verbatim — day numbers, hotel names, landmarks, prices, inclusions, notes.",
      },
      {
        promptId: "itinerary_edit_parse_content",
        label: "Step 2 — Parse Extracted Text → Itinerary JSON",
        note: "Converts the full extracted/OCR'd itinerary text into the structured itinerary JSON schema. Every day, hotel, inclusion, price tier, and note is preserved faithfully.",
      },
      {
        promptId: "itinerary_edit_parse_user_instructions",
        label: "Step 3 — Parse User Edit Request",
        note: "Seller sends a follow-up edit message (e.g. 'add return flight, change day 3 to ski resort'). This step decides what to change or whether to ask a clarifying question.",
      },
      {
        promptId: "itinerary_edit_patch",
        label: "Step 4 — Apply Patch / Clarification",
        optional: true,
        note: "Once the edit request is clear, produces a JSON patch of only the changed fields. The frontend merges this patch into the live itinerary JSON.",
      },
    ],
  },

  // ── RAG v2 Chat ──────────────────────────────────────────────────────────────
  {
    id: "rag_v2_chat",
    groupId: "rag",
    title: "RAG v2 Chat — Full Pipeline",
    description:
      "Every buyer chat message flows through: Router (classify intent + extract filters) → Pinecone search → Generator (write reply + chips) → periodic Compression. Enquiry refinement runs on demand.",
    steps: [
      {
        promptId: "rag_attachment_extract",
        label: "Step 0 — Attachment Reader: Read an Uploaded Itinerary / Quote / Ticket (conditional)",
        optional: true,
        note: "Runs ONLY when the message carries attached files (images, PDF, Word) that have not been read yet. Extracts everything as JSON; the summary is shown to the user and the trip details join the chat's gathered info before the router runs. Safety rules (file text is data, never instructions; no ID/passport/card numbers) are added by the code and are not editable here.",
      },
      {
        promptId: "rag_router",
        label: "Step 1 — Router: Classify Intent + Extract Filters",
        roleGate: "buyer",
        note: "Runs for every message from a BUYER (session.personalization_profile.is_seller = false). Returns ~20 JSON fields: action type (search/compare/connect/enquiry/itinerary…), filters (destination, budget, dates, pax), category namespace, and flags controlling every downstream step.",
      },
      {
        promptId: "rag_router_seller",
        label: "Step 1 — Router: Classify Intent + Extract Filters",
        roleGate: "seller",
        note: "Runs instead of the Buyer router for every message from a logged-in SELLER — never both, never skipped, decided per-message by is_seller. Fully live and editable, not a fallback stub: until you save an override here it reuses the buyer prompt/model/temperature/tokens as its default, then diverges the moment you edit it. Competitor isolation (sellers never see other sellers' own-market deals/leads/DMC partners) is separate code-level filtering in pipeline.py + retrieve.py, unaffected by anything edited here.",
      },
      {
        promptId: "rag_knowledge",
        label: "Step 2 — Web Knowledge: Perplexity Travel Research (conditional)",
        optional: true,
        note: "Runs ONLY when the router marks action=travel_knowledge with web_search=true (advisory questions like \"best time to visit X\"). Calls Perplexity Sonar for current web info; runs BEFORE the Pinecone probe so its named destinations can gate the inventory search.",
      },
      {
        type: "info",
        icon: "🔷",
        label: "Step 3 — Pinecone Semantic Search",
        note: "Not an LLM prompt — the router's extracted filters + namespace (or Perplexity's named destinations, for travel_knowledge) are used to query Pinecone. The top-K matching deal vectors are retrieved and enriched from MongoDB.",
      },
      {
        promptId: "rag_generator",
        label: "Step 4 — Generator: Write Bot Reply",
        roleGate: "buyer",
        note: "Runs for every BUYER turn. Takes the router decision + retrieved deals and writes the actual response the buyer sees. Handles all action types. Always ends with a [CHIPS] block of 2–4 follow-up suggestion buttons — generated inline by this same call, no separate LLM call.",
      },
      {
        promptId: "rag_generator_seller",
        label: "Step 4 — Generator: Write Bot Reply",
        roleGate: "seller",
        note: "Runs instead of the Buyer generator for every SELLER turn — never both, never skipped. Fully live and editable: until you save an override here it reuses the buyer prompt/model/temperature/tokens as its default, then diverges the moment you edit it. Deals shown are already competitor-filtered upstream (own-market queries restrict Pinecone to the seller's own seller_ids, and DMC/partner suggestions are suppressed entirely) — this prompt only controls tone/wording, it never re-adds competitor deals.",
      },
      {
        promptId: "rag_compression",
        label: "Step 5 — Compression: Fold Old History (periodic)",
        optional: true,
        note: "Runs automatically when the session turn count crosses a threshold. Compresses older turns into a compact JSON summary injected at the start of all future prompts.",
      },
      {
        promptId: "rag_enquiry_refine",
        label: "Step 6 — Enquiry Refine (on demand)",
        optional: true,
        note: "Runs when the buyer clicks 'Refine' on a generated enquiry draft. Rewrites the enquiry to be professional and clear, expands placeholder fields into polite follow-up asks.",
      },
    ],
  },
  // ── Market Research ──────────────────────────────────────────────────────
  {
    id: "market_research_chat",
    groupId: "market_research",
    title: "Market Research — Chat Pipeline",
    description:
      "Buyer/seller asks a travel-market pricing/competitive question in the Market Research tab. First a cheap call figures out the destination; if we have real inventory for it (cached, or computed fresh on a cache miss), it's added as grounding alongside the compressed rolling summary before the raw question goes to Perplexity Sonar for live web search — Perplexity's own search always still runs, as a cross-check on top of our own data, never a replacement for it. After every successful reply, a cheap compression call folds the turn into the rolling summary used as CONTEXT on the next turn.",
    steps: [
      {
        promptId: "market_research_extract_destination",
        label: "Step 1 — Extract Destination (Country / City)",
        note: "Cheap JSON-only call: message → {country, city}. Figures out what the real-inventory lookup below should search for. Handles landmarks/pilgrimage sites/regions too, not just literal city names (e.g. 'Vaishno Devi' → country=India, city=Katra) — tested empirically, the model's own world knowledge resolves these correctly without needing an explicit example for every place type. On failure or no clear destination, only Step 2 (inventory) is skipped — the question still goes straight to Perplexity (Step 3), exactly as before this feature existed.",
      },
      {
        promptId: "market_research_inventory_summary",
        label: "Step 2 — Summarize Real Inventory (cache miss only)",
        optional: true,
        note: "Runs ONLY the first time a country is asked about within the Redis cache TTL (12h) — most requests hit the cache and skip this entirely. Synthesizes deals' EXISTING enriched_summary/title/price (already AI-written at upload time, never re-analyzed here) into one B2B paragraph, which then gets cached and reused for every question about that country until it expires.",
      },
      {
        promptId: "market_research_answer",
        label: "Step 3 — Answer via Perplexity Sonar",
        note: "The user-facing LLM call. Scoped to travel destination/pricing/market-research questions; grounds its answer in our real inventory when Step 2 (cached or fresh) found any, then always still does its own live web search on top — that cross-check never gets skipped.",
      },
      {
        promptId: "market_research_compress",
        label: "Step 4 — Compress Turn into Rolling Summary",
        optional: true,
        note: "Runs after every successful answer (skipped when Step 3 falls back). Folds prior summary + this turn into a rolling summary — no word cap (removed 2026-07-28, was max 60 words) — used as CONTEXT on the next turn's Perplexity call, so multi-turn conversations don't lose earlier destinations/figures.",
      },
    ],
  },
  // ── Help / Support KB ────────────────────────────────────────────────────
  // No prompt steps — this group renders <HelpKbSection /> entirely on its
  // own (see the groupId === "help_kb" branch in the main render below).
  // The entry still needs to exist so the sidebar has something to select.
  {
    id: "help_kb_main",
    groupId: "help_kb",
    title: "Help / Support KB — Manage Sources",
    description:
      "Buyer and seller help/support source content (pasted text or PDF), independently saved and reindexed, backing the RAG help-desk answers.",
    steps: [],
  },
];

const SIDEBAR_GROUPS = [
  { id: "deal_analysis", label: "Deal Analysis", icon: "🔍" },
  { id: "ai_media", label: "AI Media", icon: "🎨" },
  { id: "rag", label: "RAG v2 Chat", icon: "💬" },
  { id: "market_research", label: "Market Research", icon: "📊" },
  { id: "help_kb", label: "Help / Support KB", icon: "📚" },
];

// ── Helpers ───────────────────────────────────────────────────────────────────

const MODEL_SETTING_LABELS: Record<string, string> = {
  OPENROUTER_MODEL: "Default Model",
  OPENROUTER_MODEL_FLYER: "Flyer / Image Model",
  OPENROUTER_MODEL_VIDEO: "Video Model",
  ROUTER_MODEL: "RAG Router Model",
  GENERATOR_MODEL: "RAG Generator Model",
  COMPRESSION_MODEL: "RAG Compression Model",
  KNOWLEDGE_MODEL: "RAG Web Knowledge (Perplexity) Model",
};

function modelLabel(key: string | null | undefined) {
  if (!key) return "Default Model";
  return MODEL_SETTING_LABELS[key] ?? key;
}

function shortModel(m: string) {
  return m.length > 38 ? m.slice(0, 36) + "…" : m;
}

// Sidebar item label: strip a "<prefix> — " lead-in ONLY when it's literally
// redundant with the group header already shown above it (e.g. "Deal
// Analysis — Full Flow" under the "Deal Analysis" group header becomes "Full
// Flow"). Titles like "Flyer — Template" / "Itinerary — Upload & Edit" live
// under the umbrella "AI Media" group, where "Flyer"/"Itinerary" is NOT the
// group label — that prefix is the whole point (it's what tells the two
// media types apart in a flat list) and must never be stripped, or every
// flyer/itinerary pipeline collapses to an indistinguishable "Template" /
// "Document Upload" / "Edit Existing" with no way to tell which is which.
function sidebarPipelineLabel(title: string, groupLabel: string): string {
  const m = title.match(/^([^—]+)—\s*(.+)$/);
  if (!m) return title;
  const [, prefix, rest] = m;
  return prefix.trim().toLowerCase() === groupLabel.trim().toLowerCase() ? rest : title;
}

// Hand-picked model presets always offered in every model picker, in addition to whatever
// available_models the backend reports (the OPENROUTER_MODEL* / fresh_rag *_MODEL env values
// actually configured). These are suggestions only — the field stays free-text, so typing any
// other OpenRouter model id works too.
const EXTRA_MODEL_PRESETS: { key: string; value: string }[] = [
  { key: "PRESET_CLAUDE_OPUS_45", value: "anthropic/claude-opus-4.5" },
  { key: "PRESET_CLAUDE_SONNET_45", value: "anthropic/claude-sonnet-4.5" },
  { key: "PRESET_PERPLEXITY_SONAR", value: "perplexity/sonar" },
  { key: "PRESET_PERPLEXITY_SONAR_PRO", value: "perplexity/sonar-pro" },
  { key: "PRESET_GPT_55", value: "openai/gpt-5.5" },
  { key: "PRESET_GEMINI_3_FLASH_PREVIEW", value: "google/gemini-3-flash-preview" },
  { key: "PRESET_GEMINI_37_FLASH", value: "google/gemini-3.7-flash" },
];
const EXTRA_MODEL_LABELS: Record<string, string> = {
  PRESET_CLAUDE_OPUS_45: "Claude Opus 4.5",
  PRESET_CLAUDE_SONNET_45: "Claude Sonnet 4.5",
  PRESET_PERPLEXITY_SONAR: "Perplexity Sonar",
  PRESET_PERPLEXITY_SONAR_PRO: "Perplexity Sonar Pro",
  PRESET_GPT_55: "GPT-5.5",
  PRESET_GEMINI_3_FLASH_PREVIEW: "Gemini 3 Flash (preview)",
  PRESET_GEMINI_37_FLASH: "Gemini 3.7 Flash",
};

// Image-generation model chains (flyer_studio_image_gen, itinerary_image_gen,
// deal_auto_flyer_image_gen) only have a real, working request-builder for
// TWO code paths — see fal_client.py: models matching "gemini" + "image" in
// the id go through _try_openrouter_chat's modalities/image_config branch;
// every other model (Grok, GPT-image, ...) is routed through
// _try_grok_stream (SSE) instead, which despite its name is the generic
// working path for any non-Gemini image model. EXTRA_MODEL_PRESETS above
// (Claude/Perplexity/GPT text models) have no image-generation path at all
// and must never be suggested here. This list is the curated, actually-
// working alternative shown ONLY on image-chain rows — availableModels
// below still adds any other backend-configured model that passes
// isWorkingImageModel, so a newly-configured model shows up automatically
// without needing a preset entry here.
const IMAGE_MODEL_PRESETS: { key: string; value: string; label: string }[] = [
  { key: "PRESET_GPT_IMAGE", value: "openai/gpt-5.4-image-2", label: "GPT Image" },
  { key: "PRESET_GROK_IMAGE", value: "x-ai/grok-imagine-image-quality", label: "Grok Image (quality)" },
  { key: "PRESET_GEMINI_25_FLASH_IMAGE", value: "google/gemini-2.5-flash-image", label: "Gemini 2.5 Flash Image" },
  { key: "PRESET_GEMINI_3_PRO_IMAGE", value: "google/gemini-3-pro-image-preview", label: "Gemini 3 Pro Image (preview)" },
];

/** True only for a model with an actual working code path in fal_client.py's
 *  image-generation request builders — Grok and GPT-image both go through
 *  _try_grok_stream, Gemini image-capable models go through
 *  _try_openrouter_chat's modalities branch. Mirrors the backend's
 *  _is_working_image_model. */
function isWorkingImageModel(value: string): boolean {
  const m = value.toLowerCase();
  return m.includes("grok") || (m.includes("gemini") && m.includes("image")) || (m.includes("gpt") && m.includes("image"));
}

// Editable combobox: type any model id freely, or open the styled dropdown and click a
// suggestion. The panel is rendered through a portal into document.body with `position: fixed`
// coordinates computed from the input's own bounding rect — every card on this page has
// `overflow-hidden` (for rounded corners), which was clipping/hiding an absolutely-positioned
// panel nested inside it. Going through a portal escapes that entirely, regardless of how many
// nested overflow/scroll containers sit in between.
function ModelPicker({
  value,
  onChange,
  resolvedModel,
  availableModels,
  imageOnly = false,
}: {
  value: string;
  onChange: (v: string) => void;
  resolvedModel: string;
  availableModels: { key: string; value: string }[];
  /** Restrict suggestions to models with a real image-generation code path
   *  (Grok / Gemini-image) — set on the 3 image-gen chain cards so Claude/
   *  Perplexity/GPT (text-only presets) never appear as a "fallback" that
   *  can't actually work. The field stays free-text either way — this only
   *  changes what's suggested. */
  imageOnly?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState<{ top: number; left: number; width: number; openUp: boolean } | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const PANEL_MAX_HEIGHT = 224; // px — matches max-h-56 below

  const reposition = useCallback(() => {
    const el = inputRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const spaceBelow = window.innerHeight - r.bottom;
    const openUp = spaceBelow < PANEL_MAX_HEIGHT && r.top > spaceBelow;
    setRect({
      top: openUp ? r.top : r.bottom,
      left: r.left,
      width: r.width,
      openUp,
    });
  }, []);

  useEffect(() => {
    if (!open) return;
    reposition();
    function onDocMouseDown(e: MouseEvent) {
      const t = e.target as Node;
      if (inputRef.current?.contains(t) || panelRef.current?.contains(t)) return;
      setOpen(false);
    }
    // Scroll events don't bubble but DO reach capture-phase ancestor listeners — including the
    // panel's OWN internal overflow-y-auto scroll. Without filtering that out, every wheel tick
    // while scrolling the list triggered a reposition() state update and fought the native
    // scroll, which is why scrolling felt broken/stuck. Only reposition for scrolling that
    // happens OUTSIDE the panel (e.g. the page itself scrolling under the open dropdown).
    function onWindowScroll(e: Event) {
      if (e.target instanceof Node && panelRef.current?.contains(e.target)) return;
      reposition();
    }
    document.addEventListener("mousedown", onDocMouseDown);
    window.addEventListener("scroll", onWindowScroll, true);
    window.addEventListener("resize", reposition);
    return () => {
      document.removeEventListener("mousedown", onDocMouseDown);
      window.removeEventListener("scroll", onWindowScroll, true);
      window.removeEventListener("resize", reposition);
    };
  }, [open, reposition]);

  // Build the option list — every entry from every source, never filtered against each other.
  // Earlier this de-duped by VALUE, which silently hid distinct, real settings (RAG Router
  // Model, RAG Generator Model, RAG Compression Model, Flyer/Image Model, ...) whenever they
  // happened to currently resolve to the same string — which they do right now, since none of
  // FRESH_RAG_ROUTER_MODEL/GENERATOR_MODEL/COMPRESSION_MODEL overrides are set, so they all
  // fall back to the same platform default. Same value ≠ same setting — each one is still a
  // separate knob you may want to point at a different model, so all of them must stay visible
  // regardless of what they currently resolve to. Only de-dupe on exact (key AND value) so a
  // truly identical row is never listed twice.
  const seenKeys = new Set<string>();
  const options: { key: string; label: string; value: string }[] = [];
  function addOption(key: string, label: string, value: string) {
    if (seenKeys.has(key)) return;
    seenKeys.add(key);
    options.push({ key, label, value });
  }
  if (resolvedModel) {
    addOption("resolved", `Current default — ${shortModel(resolvedModel)}`, resolvedModel);
  }
  if (imageOnly) {
    for (const m of IMAGE_MODEL_PRESETS) {
      addOption(m.key, `${m.label} — ${shortModel(m.value)}`, m.value);
    }
    for (const m of availableModels) {
      if (isWorkingImageModel(m.value)) {
        addOption(m.key, `${modelLabel(m.key)} — ${shortModel(m.value)}`, m.value);
      }
    }
  } else {
    for (const m of EXTRA_MODEL_PRESETS) {
      addOption(m.key, `${EXTRA_MODEL_LABELS[m.key]} — ${shortModel(m.value)}`, m.value);
    }
    for (const m of availableModels) {
      addOption(m.key, `${modelLabel(m.key)} — ${shortModel(m.value)}`, m.value);
    }
  }

  const q = value.trim().toLowerCase();
  const filtered = q ? options.filter((o) => o.value.toLowerCase().includes(q) || o.label.toLowerCase().includes(q)) : options;

  return (
    <>
      <input
        ref={inputRef}
        type="text"
        className="w-full bg-black/30 border border-white/10 rounded-lg px-2.5 py-1.5 text-xs text-gray-200 focus:outline-none focus:border-indigo-500/50"
        value={value}
        placeholder={shortModel(resolvedModel)}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setOpen(true)}
      />
      {open && rect && createPortal(
        <div
          ref={panelRef}
          style={{
            position: "fixed",
            left: rect.left,
            width: rect.width,
            maxHeight: PANEL_MAX_HEIGHT,
            ...(rect.openUp ? { bottom: window.innerHeight - rect.top + 4 } : { top: rect.top + 4 }),
          }}
          className="z-[1000] overflow-y-auto rounded-lg border border-white/10 bg-gray-900 shadow-2xl"
        >
          {filtered.length === 0 && (
            <div className="px-2.5 py-2 text-[11px] text-gray-600">No match — keep typing to use this as a custom model id</div>
          )}
          {filtered.map((o) => (
            <button
              key={o.key}
              type="button"
              onMouseDown={(e) => e.preventDefault()} // keep input focus so onClick still fires cleanly
              onClick={() => { onChange(o.value); setOpen(false); }}
              className={`w-full text-left px-2.5 py-1.5 text-[11px] hover:bg-white/10 transition-colors ${
                o.value === value ? "text-indigo-300 bg-indigo-500/10" : "text-gray-300"
              }`}
            >
              {o.label}
            </button>
          ))}
        </div>,
        document.body,
      )}
    </>
  );
}

// Quick way to test the generator's itinerary-boost path (action=generate_itinerary or
// build_custom_package automatically uses a higher max_tokens than the normal default). The
// boost value itself is editable here — not hardcoded — since what counts as "enough tokens
// for a full itinerary" is exactly what you're trying to tune.
function ItineraryBoostControl({ onApply }: { onApply: (tokens: number) => void }) {
  const [boost, setBoost] = useState(6000);
  return (
    <div className="mt-1 flex items-center gap-1">
      <input
        type="number"
        min={1}
        max={32000}
        value={boost}
        onChange={(e) => setBoost(parseInt(e.target.value, 10) || 1)}
        className="w-16 bg-black/30 border border-white/10 rounded-md px-1.5 py-1 text-[10px] text-gray-200 focus:outline-none focus:border-indigo-500/50"
        title="Itinerary boost value — edit this if you want to test a different cap than 6000"
      />
      <button
        type="button"
        onClick={() => onApply(boost)}
        className="flex-1 text-[9px] px-1.5 py-1 rounded-md border border-indigo-700/30 text-indigo-400 hover:bg-indigo-900/20 transition-colors"
        title="Applies the value on the left to Max Tokens above. 6000 is the code default the generator auto-uses when action=generate_itinerary or build_custom_package — edit the number to test a different cap."
      >
        Apply itinerary boost
      </button>
    </div>
  );
}

function fillVars(template: string, vars: string[], values: Record<string, string>) {
  let out = template;
  for (const name of vars) {
    out = out.split(`{${name}}`).join(values[name] ?? "");
  }
  return out;
}

// ── Per-step state ────────────────────────────────────────────────────────────

export interface StepState {
  varValues: Record<string, string>;
  systemOverride: string | null; // null = use registry default
  userOverride: string | null;
  imageUrls: string;
  model: string;
  // Full ordered model chain — only meaningful for the 3 image-generation
  // entries (flyer_studio_image_gen, itinerary_image_gen,
  // deal_auto_flyer_image_gen). One editable row per chain slot in the UI;
  // saving sends this whole array as a REPLACEMENT for the coded chain.
  modelChain: string[];
  temperature: number;
  maxTokens: number;
  running: boolean;
  output: string | null;
  usage: { prompt_tokens: number; completion_tokens: number; total_tokens: number } | null;
  error: string | null;
  expanded: boolean;
}

function makeStepState(prompt: AdminPromptEntry): StepState {
  const defaults: Record<string, string> = {};
  for (const v of prompt.variables) defaults[v.name] = v.example ?? "";
  const ov = prompt.db_override;
  // Chain field seeds from: saved override chain → coded chain's current
  // resolved values (so an admin opening this for the first time sees EVERY
  // model already wired in, e.g. Grok AND Gemini 2.5, not just the first one)
  // → single-item fallback from `model`/`resolved_model` for entries that
  // aren't chains at all (harmless — never rendered for those).
  const modelChain =
    (ov?.model_chain && ov.model_chain.length > 0 ? ov.model_chain : null)
    ?? (prompt.resolved_chain && prompt.resolved_chain.length > 0 ? prompt.resolved_chain : null)
    ?? [(ov?.model) || prompt.resolved_model || ""];
  return {
    varValues: defaults,
    systemOverride: ov ? ov.system_prompt : null,
    userOverride: ov ? ov.user_prompt : null,
    imageUrls: "",
    model: (ov?.model) || prompt.resolved_model || "",
    modelChain,
    temperature: ov?.temperature ?? prompt.default_temperature,
    maxTokens: ov?.max_tokens ?? prompt.default_max_tokens,
    running: false,
    output: null,
    usage: null,
    error: null,
    expanded: false,
  };
}

// ── Main component ────────────────────────────────────────────────────────────

export default function AdminPromptPlaygroundPage() {
  const [data, setData] = useState<AdminPromptsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { showError } = useAdminToast();
  useEffect(() => { if (error) showError(error); }, [error, showError]);

  const [openGroup, setOpenGroup] = useState<string>("deal_analysis");
  const [activePipelineId, setActivePipelineId] = useState<string>("deal_analysis_main");
  const [ragView, setRagView] = useState<"pipeline" | "chat" | "playground">("pipeline");
  const [dealAnalysisView, setDealAnalysisView] = useState<"pipeline" | "playground">("pipeline");
  const [aiMediaView, setAiMediaView] = useState<"pipeline" | "playground">("pipeline");
  const [marketResearchView, setMarketResearchView] = useState<"pipeline" | "chat" | "playground">("pipeline");

  // per-step states keyed by promptId
  const [stepStates, setStepStates] = useState<Record<string, StepState>>({});
  const [runningAll, setRunningAll] = useState(false);

  const promptsById = useMemo<Record<string, AdminPromptEntry>>(() => {
    if (!data) return {};
    const map: Record<string, AdminPromptEntry> = {};
    for (const p of data.prompts) map[p.id] = p;
    return map;
  }, [data]);

  const activePipeline = useMemo(
    () => PIPELINES.find((p) => p.id === activePipelineId) ?? PIPELINES[0],
    [activePipelineId],
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await adminService.getAdminPrompts();
      setData(res);
    } catch (err) {
      setError(parseApiError(err).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);


  // Init step states when prompts load
  useEffect(() => {
    if (!data) return;
    setStepStates((prev) => {
      const next = { ...prev };
      for (const p of data.prompts) {
        if (!next[p.id]) next[p.id] = makeStepState(p);
      }
      return next;
    });
  }, [data]);

  // Reset step outputs when pipeline changes
  const prevPipelineId = useRef(activePipelineId);
  useEffect(() => {
    if (prevPipelineId.current === activePipelineId) return;
    prevPipelineId.current = activePipelineId;
    setStepStates((prev) => {
      const next = { ...prev };
      for (const step of activePipeline.steps) {
        if (step.type === "info") continue;
        const id = step.promptId;
        if (next[id]) {
          next[id] = { ...next[id], output: null, error: null, usage: null };
        }
      }
      return next;
    });
  }, [activePipelineId, activePipeline]);

  function updateStep(promptId: string, patch: Partial<StepState>) {
    setStepStates((prev) => ({ ...prev, [promptId]: { ...prev[promptId], ...patch } }));
  }

  function handleOverrideSaved(promptId: string, override: import("../../features/admin/types").AdminPromptDbOverride) {
    setData((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        prompts: prev.prompts.map((p) => p.id === promptId ? { ...p, db_override: override } : p),
        categories: prev.categories.map((cat) => ({
          ...cat,
          prompts: cat.prompts.map((p) => p.id === promptId ? { ...p, db_override: override } : p),
          subcategories: cat.subcategories.map((sub) => ({
            ...sub,
            prompts: sub.prompts.map((p) => p.id === promptId ? { ...p, db_override: override } : p),
          })),
        })),
      };
    });
  }

  async function runStep(promptId: string) {
    const prompt = promptsById[promptId];
    const ss = stepStates[promptId];
    if (!prompt || !ss) return;
    const varNames = prompt.variables.map((v) => v.name);
    const resolvedSystem = fillVars(ss.systemOverride ?? prompt.system_prompt, varNames, ss.varValues);
    const resolvedUser = fillVars(ss.userOverride ?? prompt.user_prompt, varNames, ss.varValues);
    const urls = ss.imageUrls.split("\n").map((u) => u.trim()).filter(Boolean);
    updateStep(promptId, { running: true, output: null, error: null, usage: null });
    try {
      const res = await adminService.runAdminPrompt({
        system_prompt: resolvedSystem,
        user_prompt: resolvedUser,
        model: ss.model.trim() || null,
        image_urls: urls.length ? urls : null,
        temperature: ss.temperature,
        max_tokens: ss.maxTokens,
      });
      updateStep(promptId, { running: false, output: res.output, usage: res.usage });
    } catch (err) {
      updateStep(promptId, { running: false, error: parseApiError(err).message });
    }
  }

  async function runAll() {
    setRunningAll(true);
    for (const step of activePipeline.steps) {
      if (step.type === "info") continue;
      const { promptId } = step;
      if (!promptsById[promptId]) continue;
      await runStep(promptId);
    }
    setRunningAll(false);
  }

  function applyUploadResult(
    result: { file_type: string; text?: string; image_data_urls?: string[] },
    autoFill: UploadAutoFill,
  ) {
    setStepStates((prev) => {
      const next = { ...prev };
      const dataUrls = (result.image_data_urls ?? []).join("\n");

      if (result.file_type === "image" && autoFill.imageTarget) {
        for (const pid of autoFill.imageTarget) {
          if (next[pid]) next[pid] = { ...next[pid], imageUrls: dataUrls, expanded: true };
        }
      }
      if (result.file_type === "video" && autoFill.videoTarget) {
        for (const pid of autoFill.videoTarget) {
          if (next[pid]) next[pid] = { ...next[pid], imageUrls: dataUrls, expanded: true };
        }
      }
      if (result.file_type === "document" && autoFill.documentTarget && result.text) {
        const { promptId: pid, varName } = autoFill.documentTarget;
        if (next[pid]) {
          next[pid] = {
            ...next[pid],
            varValues: { ...next[pid].varValues, [varName]: result.text },
            expanded: true,
          };
        }
      }
      return next;
    });
  }

  if (loading) return (
    <div className="flex items-center justify-center h-full">
      <div className="text-gray-400 text-sm">Loading prompt registry…</div>
    </div>
  );
  if (error) return <div className="p-6 text-red-400 text-sm">{error}</div>;
  if (!data) return null;

  const groupPipelines = (gid: string) => PIPELINES.filter((p) => p.groupId === gid);

  return (
    <div className="flex h-full min-h-0 bg-gray-950">

      {/* ── Sidebar ── */}
      <div className="w-64 shrink-0 border-r border-white/8 overflow-y-auto flex flex-col">
        <div className="px-4 py-3 border-b border-white/8 flex items-start justify-between gap-2">
          <div>
            <h2 className="text-xs font-bold uppercase tracking-widest text-gray-500">Prompt Pipelines</h2>
            <p className="text-[10px] text-gray-600 mt-0.5">{PIPELINES.length} flows · {data.prompts.length} prompts</p>
          </div>
          <RefreshButton onRefresh={load} className="px-2 py-1 text-[10px]" />
        </div>

        <div className="flex-1 overflow-y-auto py-2">
          {SIDEBAR_GROUPS.map((group) => {
            const pipelines = groupPipelines(group.id);
            const isOpen = openGroup === group.id;
            return (
              <div key={group.id} className="mb-1">
                <button
                  onClick={() => setOpenGroup(isOpen ? "" : group.id)}
                  className={`w-full text-left flex items-center gap-2 px-3 py-2 mx-1 rounded-lg transition-colors ${
                    isOpen ? "bg-white/8 text-white" : "hover:bg-white/5 text-gray-400"
                  }`}
                >
                  <span className="text-base leading-none">{group.icon}</span>
                  <span className="flex-1 text-xs font-semibold">{group.label}</span>
                  <span className="text-[10px] text-gray-600 font-mono">{pipelines.length}</span>
                  <span className={`text-gray-600 text-xs transition-transform ${isOpen ? "rotate-90" : ""}`}>›</span>
                </button>

                {isOpen && (
                  <div className="mx-2 mt-1 mb-2 space-y-0.5">
                    {pipelines.map((pl) => {
                      const isActive = activePipelineId === pl.id;
                      const stepCount = pl.steps.filter((s) => s.type !== "info").length;
                      return (
                        <button
                          key={pl.id}
                          onClick={() => { setActivePipelineId(pl.id); }}
                          className={`w-full text-left px-3 py-2.5 rounded-lg transition-colors border ${
                            isActive
                              ? "bg-indigo-600/25 border-indigo-500/30 text-indigo-200"
                              : "hover:bg-white/5 border-transparent text-gray-300"
                          }`}
                        >
                          <div className="text-xs font-medium leading-snug">{sidebarPipelineLabel(pl.title, group.label)}</div>
                          <div className="text-[10px] text-gray-600 mt-0.5">{pl.steps.length} steps · {stepCount} LLM calls</div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Pipeline Flow ── */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-4xl mx-auto p-6">

          {/* Deal Analysis group: Pipeline | Analysis Playground tab switcher */}
          {activePipeline.groupId === "deal_analysis" && (
            <div className="flex items-center gap-1 mb-5 p-1 bg-white/5 rounded-xl border border-white/8 w-fit">
              <button
                onClick={() => setDealAnalysisView("pipeline")}
                className={`px-4 py-2 rounded-lg text-xs font-semibold transition-colors ${dealAnalysisView === "pipeline" ? "bg-indigo-600 text-white" : "text-gray-400 hover:text-white"}`}
              >
                🔬 Pipeline View
              </button>
              <button
                onClick={() => setDealAnalysisView("playground")}
                className={`px-4 py-2 rounded-lg text-xs font-semibold transition-colors ${dealAnalysisView === "playground" ? "bg-indigo-600 text-white" : "text-gray-400 hover:text-white"}`}
              >
                🧪 Analysis Playground
              </button>
            </div>
          )}

          {/* Deal Analysis playground */}
          {activePipeline.groupId === "deal_analysis" && dealAnalysisView === "playground" && (
            <DealAnalysisPlayground prompts={data.prompts} />
          )}

          {/* AI Media group: Pipeline | Generation Playground tab switcher */}
          {activePipeline.groupId === "ai_media" && (
            <div className="flex items-center gap-1 mb-5 p-1 bg-white/5 rounded-xl border border-white/8 w-fit">
              <button
                onClick={() => setAiMediaView("pipeline")}
                className={`px-4 py-2 rounded-lg text-xs font-semibold transition-colors ${aiMediaView === "pipeline" ? "bg-indigo-600 text-white" : "text-gray-400 hover:text-white"}`}
              >
                🔬 Pipeline View
              </button>
              <button
                onClick={() => setAiMediaView("playground")}
                className={`px-4 py-2 rounded-lg text-xs font-semibold transition-colors ${aiMediaView === "playground" ? "bg-indigo-600 text-white" : "text-gray-400 hover:text-white"}`}
              >
                🎨 Generation Playground
              </button>
            </div>
          )}

          {/* AI Media generation playground */}
          {activePipeline.groupId === "ai_media" && aiMediaView === "playground" && (
            <AiMediaPlayground prompts={data.prompts} />
          )}

          {/* RAG group: Pipeline | Prompt Playground | Chat tab switcher */}
          {activePipeline.groupId === "rag" && (
            <div className="flex items-center gap-1 mb-5 p-1 bg-white/5 rounded-xl border border-white/8 w-fit">
              <button
                onClick={() => setRagView("pipeline")}
                className={`px-4 py-2 rounded-lg text-xs font-semibold transition-colors ${ragView === "pipeline" ? "bg-indigo-600 text-white" : "text-gray-400 hover:text-white"}`}
              >
                🔬 Pipeline View
              </button>
              <button
                onClick={() => setRagView("playground")}
                className={`px-4 py-2 rounded-lg text-xs font-semibold transition-colors ${ragView === "playground" ? "bg-indigo-600 text-white" : "text-gray-400 hover:text-white"}`}
              >
                🧪 Prompt Playground
              </button>
              <button
                onClick={() => setRagView("chat")}
                className={`px-4 py-2 rounded-lg text-xs font-semibold transition-colors ${ragView === "chat" ? "bg-indigo-600 text-white" : "text-gray-400 hover:text-white"}`}
              >
                💬 Chat Playground
              </button>
            </div>
          )}

          {/* Single-prompt sandbox for RAG group — pick one prompt, edit + run it standalone */}
          {activePipeline.groupId === "rag" && ragView === "playground" && (
            <PromptSandbox
              title="RAG v2 Prompt Playground"
              subtitle="Edit and run ONE prompt in isolation — not a full chat turn"
              selectorLabel="RAG Prompt"
              audienceLabel="buyers"
              options={RAG_PROMPT_OPTIONS}
              prompts={data.prompts}
              stepStates={stepStates}
              updateStep={updateStep}
              runStep={runStep}
              availableModels={data.available_models}
              onOverrideSaved={handleOverrideSaved}
            />
          )}

          {/* Chat playground for RAG group */}
          {activePipeline.groupId === "rag" && ragView === "chat" && (
            <RagChatPlayground overridePromptIds={["rag_router", "rag_router_seller", "rag_generator", "rag_generator_seller", "rag_compression", "rag_knowledge"]} prompts={data.prompts} />
          )}

          {/* Market Research group: Pipeline | Prompt Playground | Chat tab switcher */}
          {activePipeline.groupId === "market_research" && (
            <div className="flex items-center gap-1 mb-5 p-1 bg-white/5 rounded-xl border border-white/8 w-fit">
              <button
                onClick={() => setMarketResearchView("pipeline")}
                className={`px-4 py-2 rounded-lg text-xs font-semibold transition-colors ${marketResearchView === "pipeline" ? "bg-indigo-600 text-white" : "text-gray-400 hover:text-white"}`}
              >
                🔬 Pipeline View
              </button>
              <button
                onClick={() => setMarketResearchView("playground")}
                className={`px-4 py-2 rounded-lg text-xs font-semibold transition-colors ${marketResearchView === "playground" ? "bg-indigo-600 text-white" : "text-gray-400 hover:text-white"}`}
              >
                🧪 Prompt Playground
              </button>
              <button
                onClick={() => setMarketResearchView("chat")}
                className={`px-4 py-2 rounded-lg text-xs font-semibold transition-colors ${marketResearchView === "chat" ? "bg-indigo-600 text-white" : "text-gray-400 hover:text-white"}`}
              >
                💬 Chat Playground
              </button>
            </div>
          )}

          {/* Single-prompt sandbox for Market Research group */}
          {activePipeline.groupId === "market_research" && marketResearchView === "playground" && (
            <PromptSandbox
              title="Market Research Prompt Playground"
              subtitle="Edit and run ONE prompt in isolation — not a full chat turn"
              selectorLabel="Market Research Prompt"
              audienceLabel="users"
              options={MARKET_RESEARCH_PROMPT_OPTIONS}
              prompts={data.prompts}
              stepStates={stepStates}
              updateStep={updateStep}
              runStep={runStep}
              availableModels={data.available_models}
              onOverrideSaved={handleOverrideSaved}
            />
          )}

          {/* Chat playground for Market Research group */}
          {activePipeline.groupId === "market_research" && marketResearchView === "chat" && (
            <MarketResearchChatPlayground prompts={data.prompts} stepStates={stepStates} />
          )}

          {/* Help / Support KB — fully custom view, no prompt steps at all */}
          {activePipeline.groupId === "help_kb" && (
            <HelpKbSection
              prompts={data.prompts}
              stepStates={stepStates}
              availableModels={data.available_models}
              onUpdate={(promptId, patch) => updateStep(promptId, patch)}
              onRun={(promptId) => runStep(promptId)}
              onOverrideSaved={handleOverrideSaved}
            />
          )}

          {/* Pipeline view (hidden when a playground/sandbox view is active) */}
          {!(activePipeline.groupId === "rag" && (ragView === "chat" || ragView === "playground")) &&
           !(activePipeline.groupId === "deal_analysis" && dealAnalysisView === "playground") &&
           !(activePipeline.groupId === "ai_media" && aiMediaView === "playground") &&
           !(activePipeline.groupId === "market_research" && (marketResearchView === "chat" || marketResearchView === "playground")) &&
           activePipeline.groupId !== "help_kb" && (
          <>

          {/* Pipeline header */}
          <div className="mb-6">
            <AdminPageHeader
              className="mb-0"
              title={activePipeline.title}
              subtitle={activePipeline.description}
              actions={
                <button
                  onClick={runAll}
                  disabled={runningAll}
                  className="shrink-0 mt-0.5 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-40 disabled:cursor-not-allowed text-white text-xs font-bold rounded-lg px-4 py-2.5 transition-colors whitespace-nowrap"
                >
                  {runningAll ? "Running Pipeline…" : "▶▶  Run Full Pipeline"}
                </button>
              }
            />

            {/* Legend */}
            <div className="flex items-center gap-3 mt-3 flex-wrap">
              <span className="flex items-center gap-1.5 text-[10px] text-gray-500">
                <span className="w-3 h-3 rounded-full bg-indigo-600/50 border border-indigo-500/50 inline-block" />
                LLM Prompt
              </span>
              <span className="flex items-center gap-1.5 text-[10px] text-gray-500">
                <span className="w-3 h-3 rounded-full bg-blue-900/50 border border-blue-600/30 inline-block" />
                System Step (no prompt)
              </span>
              <span className="flex items-center gap-1.5 text-[10px] text-gray-500">
                <span className="w-3 h-3 rounded bg-white/5 border border-white/10 inline-block" />
                Optional
              </span>
            </div>
          </div>

          {/* Payload uploader — shown only for pipelines that support file input */}
          {activePipeline.uploadConfig && (
            <div className="mb-6">
              <PayloadUploader
                config={activePipeline.uploadConfig}
                onResult={(result) => applyUploadResult(result, activePipeline.uploadConfig!.autoFill)}
              />
            </div>
          )}

          {/* Steps */}
          <div className="space-y-0">
            {activePipeline.steps.map((step, idx) => {
              const isLast = idx === activePipeline.steps.length - 1;

              if (step.type === "info") {
                // Image-generation model-chain steps carry a promptId (one of the
                // 3 image-gen registry entries) — render the editable Model card
                // instead of a static info box. Every other "info" step (OCR,
                // Pinecone indexing, PDF render, ...) has no promptId and stays
                // as a plain non-editable card.
                if (step.promptId) {
                  return (
                    <div key={idx}>
                      <ImageModelStepCard
                        step={step}
                        index={idx}
                        prompt={promptsById[step.promptId]}
                        ss={stepStates[step.promptId]}
                        availableModels={data.available_models}
                        onUpdate={(patch) => updateStep(step.promptId!, patch)}
                        onOverrideSaved={handleOverrideSaved}
                        audienceLabel="this pipeline's image generation"
                      />
                      {!isLast && <StepConnector />}
                    </div>
                  );
                }
                return (
                  <div key={idx}>
                    <InfoStepCard step={step} index={idx} />
                    {!isLast && <StepConnector />}
                  </div>
                );
              }

              const prompt = promptsById[step.promptId];
              const ss = stepStates[step.promptId];

              return (
                <div key={step.promptId}>
                  <PromptStepCard
                    step={step}
                    index={idx}
                    prompt={prompt}
                    ss={ss}
                    availableModels={data.available_models}
                    onUpdate={(patch) => updateStep(step.promptId, patch)}
                    onRun={() => runStep(step.promptId)}
                    onOverrideSaved={handleOverrideSaved}
                  />
                  {!isLast && <StepConnector />}
                </div>
              );
            })}
          </div>

          </>
          )}

        </div>
      </div>
    </div>
  );
}

// ── RAG Chat Playground ───────────────────────────────────────────────────────

interface AdminDeal {
  id?: string;
  deal_id?: string;
  title?: string;
  description?: string;
  item_type?: string;
  image_url?: string;
  image?: string;
  image_clean?: string;
  media?: { image?: string | null; image_clean?: string | null; video_url?: string | null };
  video_url?: string;
  pricing?: { original_price?: number; discounted_price?: number; currency?: string };
  destination?: { cities?: string[]; countries?: string[] };
  package?: { duration_nights?: number; highlights?: string[]; inclusions?: string[] };
  seller_name?: string;
  seller_whatsapp?: string;
  seller_email?: string;
  dealer_id?: string;
  is_featured?: boolean;
}

interface AdminDmcCard {
  dealer_id: string;
  seller_name: string;
  covered?: string[];
  destinations?: string[];
  cities?: string[];
  countries?: string[];
  segments?: string[];
  tags?: string[];
  avatar_url?: string;
  seller_match?: { score?: number; reasons?: string[] };
}

interface RagChatMessage {
  role: "user" | "assistant";
  text: string;
  streaming?: boolean;
  deals?: AdminDeal[];
  cross_sell_sections?: { category: string; label: string; deals: AdminDeal[] }[];
  chips?: string[];
  action?: string;
  dmcs?: AdminDmcCard[];
}

// ── Admin Deal Card (Tailwind version) ───────────────────────────────────────

// ── Admin Deal Detail Modal ───────────────────────────────────────────────────

function AdminDealDetailModal({ deal, onClose }: { deal: AdminDeal; onClose: () => void }) {
  const [rawDoc, setRawDoc] = useState<Record<string, unknown> | null>(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState<"summary" | "enquiry" | "raw">("summary");
  const dealId = deal.id ?? deal.deal_id ?? "";

  useEffect(() => {
    if (!dealId) return;
    setLoading(true);
    adminService.getDealDetailForPlayground(dealId)
      .then(d => setRawDoc(d))
      .catch(() => setRawDoc(null))
      .finally(() => setLoading(false));
  }, [dealId]);

  const poster = deal.image_url || deal.media?.image_clean || deal.media?.image || deal.image_clean || deal.image;
  const price = deal.pricing?.discounted_price ?? deal.pricing?.original_price;
  const dest = deal.destination ? [...(deal.destination.cities ?? []), ...(deal.destination.countries ?? [])].join(", ") : "";
  const enquiryDraft = rawDoc ? buildEnquiryDraft(deal, rawDoc) : "";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <div className="w-full max-w-3xl max-h-[90vh] bg-gray-900 border border-white/10 rounded-2xl overflow-hidden flex flex-col shadow-2xl" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-start gap-3 p-4 border-b border-white/8 shrink-0">
          {poster && <img src={poster} alt={deal.title} className="w-20 h-14 rounded-lg object-cover shrink-0" />}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-white leading-snug">{deal.title || "Untitled Deal"}</p>
            <p className="text-[10px] text-gray-500 mt-0.5 font-mono">{dealId}</p>
            <div className="flex gap-1.5 mt-1 flex-wrap">
              {deal.item_type && <span className="text-[9px] bg-indigo-900/50 text-indigo-300 border border-indigo-700/30 rounded px-1.5 py-0.5">{deal.item_type}</span>}
              {dest && <span className="text-[9px] text-gray-500">📍 {dest}</span>}
              {price && <span className="text-[9px] text-emerald-400 font-bold">₹{price.toLocaleString("en-IN")}</span>}
            </div>
          </div>
          <button onClick={onClose} className="shrink-0 text-gray-500 hover:text-white text-lg leading-none mt-0.5">✕</button>
        </div>
        {/* Tabs */}
        <div className="flex border-b border-white/8 shrink-0">
          {(["summary", "enquiry", "raw"] as const).map(t => (
            <button key={t} onClick={() => setTab(t)} className={`px-4 py-2.5 text-[11px] font-medium border-b-2 transition-colors capitalize ${tab === t ? "border-indigo-500 text-indigo-300" : "border-transparent text-gray-500 hover:text-gray-300"}`}>
              {t === "enquiry" ? "Enquiry Draft" : t === "raw" ? "Full MongoDB Doc" : "Summary"}
            </button>
          ))}
        </div>
        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading && <p className="text-[11px] text-gray-500">Loading full document…</p>}
          {tab === "summary" && (
            <div className="space-y-3">
              <DetailSection title="Description" value={deal.description} />
              {deal.package?.highlights && deal.package.highlights.length > 0 && (
                <div>
                  <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-1">Highlights</p>
                  <ul className="space-y-0.5">
                    {deal.package.highlights.map((h, i) => <li key={i} className="text-[11px] text-gray-300 flex gap-1.5"><span className="text-indigo-400 shrink-0">•</span>{h}</li>)}
                  </ul>
                </div>
              )}
              {deal.package?.inclusions && deal.package.inclusions.length > 0 && (
                <div>
                  <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-1">Inclusions</p>
                  <ul className="space-y-0.5">
                    {deal.package.inclusions.map((h, i) => <li key={i} className="text-[11px] text-gray-300 flex gap-1.5"><span className="text-emerald-400 shrink-0">✓</span>{h}</li>)}
                  </ul>
                </div>
              )}
              <DetailSection title="Seller" value={deal.seller_name} />
              {deal.seller_whatsapp && <DetailSection title="WhatsApp" value={deal.seller_whatsapp} />}
              {deal.seller_email && <DetailSection title="Email" value={deal.seller_email} />}
              {rawDoc && Object.entries(rawDoc).filter(([k]) => !["_id","id","deal_id","title","description","media","image","image_url","image_clean","video_url","seller_name","seller_whatsapp","seller_email","pricing","destination","package","item_type","is_featured","dealer_id"].includes(k)).slice(0, 20).map(([k, v]) => (
                <DetailSection key={k} title={k} value={typeof v === "object" ? JSON.stringify(v, null, 2) : String(v ?? "")} mono={typeof v === "object"} />
              ))}
            </div>
          )}
          {tab === "enquiry" && (
            <div>
              <p className="text-[10px] text-gray-500 mb-3">This is the enquiry draft that would be sent to the seller when a buyer clicks "Send Enquiry" on this deal.</p>
              <pre className="whitespace-pre-wrap text-[11px] text-gray-200 bg-black/30 border border-white/8 rounded-xl p-4 leading-relaxed font-mono">{enquiryDraft || "No enquiry draft available."}</pre>
              <div className="mt-3 flex gap-2">
                {deal.seller_whatsapp && (
                  <a href={`https://wa.me/${deal.seller_whatsapp}?text=${encodeURIComponent(enquiryDraft)}`} target="_blank" rel="noopener noreferrer" className="text-[10px] bg-emerald-700/30 hover:bg-emerald-700/50 border border-emerald-700/40 text-emerald-300 rounded-lg px-3 py-1.5 transition-colors">
                    📲 Test on WhatsApp
                  </a>
                )}
                {deal.seller_email && (
                  <a href={`mailto:${deal.seller_email}?subject=Travel Enquiry — ${deal.title}&body=${encodeURIComponent(enquiryDraft)}`} target="_blank" rel="noopener noreferrer" className="text-[10px] bg-blue-700/30 hover:bg-blue-700/50 border border-blue-700/40 text-blue-300 rounded-lg px-3 py-1.5 transition-colors">
                    ✉️ Test via Email
                  </a>
                )}
              </div>
            </div>
          )}
          {tab === "raw" && (
            <pre className="whitespace-pre-wrap text-[10px] text-gray-300 bg-black/30 border border-white/8 rounded-xl p-4 leading-relaxed font-mono">
              {rawDoc ? JSON.stringify(rawDoc, null, 2) : loading ? "Loading…" : "Failed to load."}
            </pre>
          )}
        </div>
      </div>
    </div>
  );
}

function DetailSection({ title, value, mono }: { title: string; value?: string | null; mono?: boolean }) {
  if (!value) return null;
  return (
    <div>
      <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-0.5">{title}</p>
      <p className={`text-[11px] text-gray-300 ${mono ? "font-mono whitespace-pre-wrap bg-black/20 rounded p-1.5" : "leading-relaxed"}`}>{value}</p>
    </div>
  );
}

function buildEnquiryDraft(deal: AdminDeal, raw: Record<string, unknown>): string {
  const dest = deal.destination ? [...(deal.destination.cities ?? []), ...(deal.destination.countries ?? [])].join(", ") : "";
  const price = deal.pricing?.discounted_price ?? deal.pricing?.original_price;
  const dur = deal.package?.duration_nights;
  const inclusions = (deal.package?.inclusions ?? []).slice(0, 6).join(", ");
  const enriched = (raw.enriched_summary as string) || (raw.description as string) || deal.description || "";
  const validity = (raw.validity as Record<string, string> | undefined);

  const lines = [
    `Hi ${deal.seller_name || "Team"},`,
    ``,
    `I'm interested in the following deal and would like more information:`,
    ``,
    `📦 Deal: ${deal.title || "—"}`,
    dest ? `📍 Destination: ${dest}` : null,
    dur ? `🕐 Duration: ${dur} Nights` : null,
    price ? `💰 Price: ₹${price.toLocaleString("en-IN")} per person` : null,
    inclusions ? `✅ Inclusions: ${inclusions}` : null,
    validity?.valid_from ? `📅 Validity: ${validity.valid_from} – ${validity.valid_until ?? "open"}` : null,
    ``,
    enriched ? `About this deal:\n${enriched.slice(0, 400)}${enriched.length > 400 ? "…" : ""}` : null,
    ``,
    `Please share availability, group rates, and booking terms.`,
    ``,
    `Thank you.`,
  ].filter(l => l !== null).join("\n");
  return lines;
}

function AdminDealCard({ deal, index, onClick }: { deal: AdminDeal; index: number; onClick: () => void }) {
  const [imgFailed, setImgFailed] = useState(false);
  const poster = deal.image_url || deal.media?.image_clean || deal.media?.image || deal.image_clean || deal.image;
  const videoSrc = deal.video_url || deal.media?.video_url;
  const price = deal.pricing?.discounted_price ?? deal.pricing?.original_price;
  const dest = deal.destination ? [...(deal.destination.cities ?? []), ...(deal.destination.countries ?? [])].join(", ") : "";
  return (
    <div
      className="shrink-0 w-52 rounded-xl border border-white/10 bg-gray-900/80 overflow-hidden flex flex-col cursor-pointer hover:border-indigo-500/40 hover:bg-gray-800/80 transition-all"
      onClick={onClick}
    >
      <div className="relative h-28 bg-gray-800 flex items-center justify-center overflow-hidden">
        {videoSrc ? (
          <video src={videoSrc} className="w-full h-full object-cover" preload="metadata" />
        ) : poster && !imgFailed ? (
          <img src={poster} alt={deal.title} className="w-full h-full object-cover" loading="lazy" onError={() => setImgFailed(true)} />
        ) : (
          <span className="text-3xl">🌍</span>
        )}
        <span className="absolute top-1.5 left-1.5 text-[9px] bg-black/60 text-white rounded px-1.5 py-0.5">#{index + 1}</span>
        {deal.item_type && <span className="absolute top-1.5 right-1.5 text-[9px] bg-indigo-900/80 text-indigo-200 rounded px-1.5 py-0.5">{deal.item_type}</span>}
        {deal.is_featured && <span className="absolute bottom-1.5 left-1.5 text-[9px] bg-yellow-900/80 text-yellow-200 rounded px-1.5 py-0.5">⭐ Featured</span>}
        <span className="absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100 bg-black/40 transition-opacity text-[10px] text-white font-medium">View Details</span>
      </div>
      <div className="p-2.5 flex flex-col gap-1 flex-1">
        <p className="text-[11px] font-semibold text-white leading-snug line-clamp-2">{deal.title || "Untitled"}</p>
        {dest && <p className="text-[10px] text-gray-500">📍 {dest}</p>}
        {deal.package?.duration_nights && <p className="text-[10px] text-gray-500">🕐 {deal.package.duration_nights}N</p>}
        {deal.description && <p className="text-[10px] text-gray-400 line-clamp-2 mt-0.5">{deal.description}</p>}
        <div className="mt-auto pt-1.5">
          {price ? (
            <p className="text-[11px] font-bold text-emerald-400">₹{price.toLocaleString("en-IN")} <span className="text-gray-500 font-normal text-[9px]">per person</span></p>
          ) : (
            <p className="text-[10px] text-gray-600">Contact for pricing</p>
          )}
        </div>
        <p className="text-[9px] text-indigo-400/60 mt-0.5">Click to view full details →</p>
      </div>
    </div>
  );
}

function AdminDealsRail({ deals, label, onDealClick }: { deals: AdminDeal[]; label?: string; onDealClick?: (d: AdminDeal) => void }) {
  const trackRef = useRef<HTMLDivElement>(null);
  if (!deals.length) return null;
  const scroll = (dir: -1 | 1) => trackRef.current?.scrollBy({ left: dir * 220, behavior: "smooth" });
  return (
    <div className="mt-2">
      {label && <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-1.5 px-0.5">{label}</p>}
      <div className="relative">
        {deals.length > 1 && <button onClick={() => scroll(-1)} className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-black/60 hover:bg-black/80 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs -translate-x-1">‹</button>}
        <div ref={trackRef} className="flex gap-2 overflow-x-auto pb-1 scroll-smooth" style={{ scrollbarWidth: "none" }}>
          {deals.map((d, i) => <AdminDealCard key={d.id ?? d.deal_id ?? i} deal={d} index={i} onClick={() => onDealClick?.(d)} />)}
        </div>
        {deals.length > 1 && <button onClick={() => scroll(1)} className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-black/60 hover:bg-black/80 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs translate-x-1">›</button>}
      </div>
    </div>
  );
}

function AdminDmcCardUI({ dmc }: { dmc: AdminDmcCard }) {
  const covers = [...(dmc.covered ?? []), ...(dmc.destinations ?? []), ...(dmc.cities ?? []), ...(dmc.countries ?? [])];
  const uniq = Array.from(new Set(covers)).slice(0, 4);
  const segs = (dmc.segments ?? dmc.tags ?? []).slice(0, 3);
  const initial = (dmc.seller_name?.[0] ?? "?").toUpperCase();
  return (
    <div className="shrink-0 w-48 rounded-xl border border-white/10 bg-gray-900/80 p-3 flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-full bg-indigo-700/40 flex items-center justify-center text-sm font-bold text-indigo-300 shrink-0">{initial}</div>
        <p className="text-[11px] font-semibold text-white leading-snug">{dmc.seller_name}</p>
      </div>
      {uniq.length > 0 && <p className="text-[9px] text-gray-500">📍 {uniq.join(", ")}</p>}
      {segs.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {segs.map(s => <span key={s} className="text-[9px] bg-white/5 border border-white/8 rounded px-1.5 py-0.5 text-gray-400">{s}</span>)}
        </div>
      )}
      {dmc.seller_match?.score ? <p className="text-[9px] text-indigo-400">Match: {Math.round(dmc.seller_match.score * 100)}%</p> : null}
    </div>
  );
}

type AdminRagSession = { session_id: string; preview: string; message_count: number; updated_at: string };

// ── Prompt Sandbox (single-prompt playground, not chained pipeline) ───────────
// Generic — used for both RAG v2 (RAG_PROMPT_OPTIONS) and Market Research
// (MARKET_RESEARCH_PROMPT_OPTIONS). Pick one prompt from a dropdown, edit and
// run it standalone; Save writes straight to system_prompts, which both
// pipelines read live — so "Save" already means "goes live for real users".

const RAG_PROMPT_OPTIONS = [
  { id: "rag_attachment_extract", label: "Attachment Reader — Read an Uploaded File into JSON" },
  { id: "rag_router", label: "Router (Buyer) — Classify Intent + Extract Filters" },
  { id: "rag_router_seller", label: "Router (Seller) — Classify Intent + Extract Filters" },
  { id: "rag_generator", label: "Generator (Buyer) — Write Bot Reply" },
  { id: "rag_generator_seller", label: "Generator (Seller) — Write Bot Reply" },
  { id: "rag_knowledge", label: "Web Knowledge — Perplexity Travel Research" },
  { id: "rag_compression", label: "Compression — Fold Old History" },
  { id: "rag_enquiry_refine", label: "Enquiry Refine — Polish Buyer Enquiry" },
];

const MARKET_RESEARCH_PROMPT_OPTIONS = [
  { id: "market_research_extract_destination", label: "Extract Destination — Country / City from Message" },
  { id: "market_research_inventory_summary", label: "Inventory Summary — Real Deals for a Country (cache miss only)" },
  { id: "market_research_answer", label: "Answer — Perplexity Sonar Market Research" },
  { id: "market_research_compress", label: "Compress — Fold Turn into Rolling Summary" },
];

// ── Prompt override controls (Update Live / Revert / History) ────────────────
// Shared between PromptStepCard (pipeline view) and PromptSandbox (single-prompt
// playground) — same three buttons everywhere a prompt can be edited:
//   ✓ Update Live — saves your edits; takes effect immediately (writes straight
//     to system_prompts, which both RAG v2 and Market Research read live — there
//     is no separate "publish" step, Save already IS live).
//   ↺ Revert to last — one click, restores whatever was live immediately before
//     your last save (undo). Toggling it twice gets you back to where you started.
//   🕓 History — the last 5 saved versions (prompt/model/temperature/max_tokens
//     + when), each individually restorable. No admin identity is tracked —
//     single-account setup, so "who changed it" is always the same answer.
type PromptHistoryEntry = Awaited<ReturnType<typeof adminService.getPromptHistory>>["history"][number];

function PromptOverrideControls({
  prompt,
  ss,
  onUpdate,
  onOverrideSaved,
  audienceLabel = "users",
  modelOnly = false,
}: {
  prompt: AdminPromptEntry;
  ss: StepState;
  onUpdate: (patch: Partial<StepState>) => void;
  onOverrideSaved: (promptId: string, override: AdminPromptDbOverride) => void;
  audienceLabel?: string;
  // Image-generation model-chain entries (flyer_studio_image_gen,
  // itinerary_image_gen, deal_auto_flyer_image_gen) have no text prompt and
  // no temperature/max_tokens — image-gen APIs don't take those the way chat
  // completions do. When true, only `model` is ever read/saved/restored;
  // system_prompt/user_prompt/temperature/max_tokens are always sent as
  // empty/null so they never accidentally get treated as real overrides for
  // something these call sites never read.
  modelOnly?: boolean;
}) {
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const { showError } = useAdminToast();
  useEffect(() => { if (saveError) showError(saveError); }, [saveError, showError]);
  const [reverting, setReverting] = useState(false);
  const [discarding, setDiscarding] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [historyEntries, setHistoryEntries] = useState<PromptHistoryEntry[]>([]);
  const [restoringAt, setRestoringAt] = useState<number | null>(null);
  const [expandedAt, setExpandedAt] = useState<number | null>(null);

  const hasDbOverride = !!prompt.db_override;

  function applySnapshot(snapshot: PromptHistoryEntry["snapshot"]) {
    onUpdate({
      systemOverride: snapshot.system_prompt ?? "",
      userOverride: snapshot.user_prompt ?? "",
      model: snapshot.model ?? "",
      temperature: snapshot.temperature ?? prompt.default_temperature,
      maxTokens: snapshot.max_tokens ?? prompt.default_max_tokens,
    });
  }

  async function saveOverride(body: { system_prompt: string; user_prompt: string; model: string | null; model_chain?: string[] | null; temperature: number | null; max_tokens: number | null }) {
    const result = await adminService.savePromptOverride(prompt.id, body);
    onOverrideSaved(prompt.id, { ...body, updated_at: result.updated_at });
  }

  async function handleSaveOverride() {
    setSaving(true);
    setSaveError(null);
    try {
      await saveOverride(modelOnly ? {
        system_prompt: "",
        user_prompt: "",
        model: null,
        model_chain: ss.modelChain,
        temperature: null,
        max_tokens: null,
      } : {
        system_prompt: ss.systemOverride ?? prompt.system_prompt,
        user_prompt: ss.userOverride ?? prompt.user_prompt,
        model: ss.model || null,
        temperature: ss.temperature,
        max_tokens: ss.maxTokens,
      });
      if (historyOpen) await loadHistory();
    } catch (err) {
      setSaveError(parseApiError(err).message);
    } finally {
      setSaving(false);
    }
  }

  // Throws away your UNSAVED typing in the editor — does NOT touch what's saved/live.
  // Different from "Revert to last": that one changes the LIVE value (a real save,
  // undoing your last Update Live click). This one is free — no network write at
  // all beyond reading the current live value back into the editor.
  async function handleDiscard() {
    setDiscarding(true);
    setSaveError(null);
    try {
      const { override } = await adminService.getPromptOverride(prompt.id);
      onUpdate(modelOnly ? {
        modelChain:
          (override?.model_chain && override.model_chain.length > 0 ? override.model_chain : null)
          ?? (prompt.resolved_chain && prompt.resolved_chain.length > 0 ? prompt.resolved_chain : null)
          ?? [prompt.resolved_model || ""],
      } : {
        systemOverride: override?.system_prompt ?? null,
        userOverride: override?.user_prompt ?? null,
        model: override?.model ?? prompt.resolved_model ?? "",
        temperature: override?.temperature ?? prompt.default_temperature,
        maxTokens: override?.max_tokens ?? prompt.default_max_tokens,
      });
    } catch (err) {
      setSaveError(parseApiError(err).message);
    } finally {
      setDiscarding(false);
    }
  }

  async function loadHistory() {
    setHistoryLoading(true);
    setHistoryError(null);
    setExpandedAt(null); // list order shifts after every save — a stale index would show the wrong entry
    try {
      const res = await adminService.getPromptHistory(prompt.id);
      setHistoryEntries(res.history);
    } catch (err) {
      setHistoryError(parseApiError(err).message);
    } finally {
      setHistoryLoading(false);
    }
  }

  async function handleToggleHistory() {
    const next = !historyOpen;
    setHistoryOpen(next);
    if (next) await loadHistory();
  }

  async function restoreEntry(entry: PromptHistoryEntry) {
    const restoredChain = entry.snapshot.model_chain && entry.snapshot.model_chain.length > 0
      ? entry.snapshot.model_chain
      : [entry.snapshot.model ?? ""];
    await saveOverride(modelOnly ? {
      system_prompt: "",
      user_prompt: "",
      model: null,
      model_chain: restoredChain,
      temperature: null,
      max_tokens: null,
    } : {
      system_prompt: entry.snapshot.system_prompt ?? "",
      user_prompt: entry.snapshot.user_prompt ?? "",
      model: entry.snapshot.model ?? null,
      temperature: entry.snapshot.temperature ?? null,
      max_tokens: entry.snapshot.max_tokens ?? null,
    });
    if (modelOnly) {
      onUpdate({ modelChain: restoredChain });
    } else {
      applySnapshot(entry.snapshot);
    }
  }

  async function handleRestore(index: number) {
    const entry = historyEntries[index];
    if (!entry) return;
    setRestoringAt(index);
    setSaveError(null);
    try {
      await restoreEntry(entry);
      await loadHistory();
    } catch (err) {
      setSaveError(parseApiError(err).message);
    } finally {
      setRestoringAt(null);
    }
  }

  // One-click undo — always fetches fresh so it's correct even if the history panel
  // was never opened or is stale from before this session's last save.
  async function handleRevert() {
    setReverting(true);
    setSaveError(null);
    try {
      const res = await adminService.getPromptHistory(prompt.id);
      if (res.history.length === 0) {
        setSaveError("No earlier version to revert to.");
        return;
      }
      await restoreEntry(res.history[0]);
      if (historyOpen) await loadHistory();
    } catch (err) {
      setSaveError(parseApiError(err).message);
    } finally {
      setReverting(false);
    }
  }

  return (
    <div className="border-t border-white/8">
      <div className="px-4 py-3 flex items-center gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          {hasDbOverride && prompt.db_override && (
            <div className="text-[10px] text-violet-400 flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-violet-400 inline-block" />
              LIVE for {audienceLabel} — saved {(parseInstant(prompt.db_override.updated_at) ?? new Date(prompt.db_override.updated_at)).toLocaleString()}
            </div>
          )}
          {!hasDbOverride && (
            <div className="text-[10px] text-gray-600">Not yet saved to system_prompts</div>
          )}
          {saveError && <div className="text-[10px] text-red-400 mt-0.5">{saveError}</div>}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={handleDiscard}
            disabled={discarding}
            title="Throw away your unsaved typing in the editor — goes back to whatever's currently live. Nothing saved changes; use this when a test didn't work out and you haven't clicked Update Live yet."
            className="text-[10px] px-2.5 py-1.5 rounded-lg border border-white/10 text-gray-400 hover:bg-white/5 disabled:opacity-40 transition-colors font-semibold"
          >
            {discarding ? "…" : "↩ Discard edit"}
          </button>
          <button
            onClick={handleRevert}
            disabled={reverting || !hasDbOverride}
            title="Undo your last SAVE — restores whatever was live immediately before it (a real change, for when Update Live already happened and didn't work out). Click again to toggle back."
            className="text-[10px] px-2.5 py-1.5 rounded-lg border border-amber-700/40 text-amber-400 hover:bg-amber-900/20 disabled:opacity-40 transition-colors font-semibold"
          >
            {reverting ? "Reverting…" : "↺ Revert to last"}
          </button>
          <button
            onClick={handleToggleHistory}
            title="See the last 5 saved versions (prompt, model, temperature, max tokens, when) — and restore any one of them"
            className="text-[10px] px-2.5 py-1.5 rounded-lg border border-white/10 text-gray-400 hover:bg-white/5 transition-colors font-semibold"
          >
            {historyOpen ? "Hide history" : "🕓 History"}
          </button>
          <button
            onClick={handleSaveOverride}
            disabled={saving}
            title={`Save your edits — takes effect immediately for real ${audienceLabel}. Use Run/Send above first to test if you're not sure yet.`}
            className="text-[10px] px-2.5 py-1.5 rounded-lg bg-violet-600/20 border border-violet-600/40 text-violet-300 hover:bg-violet-600/30 disabled:opacity-40 transition-colors font-semibold"
          >
            {saving ? "Saving…" : "✓ Update Live"}
          </button>
        </div>
      </div>

      {historyOpen && (
        <div className="px-4 py-3 border-t border-white/8 bg-black/10">
          {historyLoading && <div className="text-[10px] text-gray-500">Loading history…</div>}
          {historyError && <div className="text-[10px] text-red-400">{historyError}</div>}
          {!historyLoading && !historyError && historyEntries.length === 0 && (
            <div className="text-[10px] text-gray-600">No prior version on record — this is the first save for this prompt.</div>
          )}
          {!historyLoading && historyEntries.length > 0 && (
            <div className="space-y-1.5 max-h-56 overflow-y-auto">
              <div className="text-[9px] text-gray-600 mb-1">Last {historyEntries.length} version{historyEntries.length > 1 ? "s" : ""} kept (oldest drop off automatically)</div>
              {historyEntries.map((h, i) => {
                const isExpanded = expandedAt === i;
                return (
                  <div key={i} className="text-[10px] text-gray-400 border-l-2 border-white/10 pl-2">
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                      <button
                        onClick={() => setExpandedAt(isExpanded ? null : i)}
                        title="Click to see the full system/user prompt text for this version, along with model, temperature, and max tokens"
                        className="min-w-0 flex-1 text-left"
                      >
                        <span className="text-gray-300 font-semibold">
                          {(parseInstant(h.replaced_at) ?? new Date(h.replaced_at)).toLocaleString()}
                        </span>
                        <span className="ml-1 text-gray-600">{isExpanded ? "▾" : "▸"}</span>
                        <div className="text-gray-500">
                          {modelOnly
                            ? <>was: chain={(h.snapshot.model_chain && h.snapshot.model_chain.length > 0 ? h.snapshot.model_chain.join(" → ") : h.snapshot.model) || "(coded default)"}</>
                            : <>was: model={h.snapshot.model || "(default)"}, temp={h.snapshot.temperature ?? "(default)"}, max_tokens={h.snapshot.max_tokens ?? "(default)"}</>}
                        </div>
                      </button>
                      <button
                        onClick={() => handleRestore(i)}
                        disabled={restoringAt !== null}
                        title="Restore this exact version — prompt, model, temperature, and max tokens — as the new live value"
                        className="shrink-0 text-[9px] px-2 py-1 rounded-md border border-amber-700/40 text-amber-400 hover:bg-amber-900/20 disabled:opacity-40 transition-colors font-semibold"
                      >
                        {restoringAt === i ? "…" : "Restore"}
                      </button>
                    </div>
                    {isExpanded && !modelOnly && (
                      <div className="mt-1.5 mb-2 space-y-1.5">
                        <div>
                          <div className="text-[9px] text-gray-600 uppercase tracking-wider">System Prompt</div>
                          <pre className="whitespace-pre-wrap text-[10px] text-gray-400 font-mono bg-black/20 rounded-lg px-2 py-1.5 max-h-32 overflow-y-auto">
                            {h.snapshot.system_prompt || "(empty)"}
                          </pre>
                        </div>
                        <div>
                          <div className="text-[9px] text-gray-600 uppercase tracking-wider">User Prompt</div>
                          <pre className="whitespace-pre-wrap text-[10px] text-gray-400 font-mono bg-black/20 rounded-lg px-2 py-1.5 max-h-32 overflow-y-auto">
                            {h.snapshot.user_prompt || "(empty)"}
                          </pre>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function PromptSandbox({
  title,
  subtitle,
  selectorLabel,
  audienceLabel = "buyers",
  options,
  prompts,
  stepStates,
  updateStep,
  runStep,
  availableModels,
  onOverrideSaved,
}: {
  title: string;
  subtitle: string;
  selectorLabel: string;
  audienceLabel?: string;
  options: { id: string; label: string }[];
  prompts: AdminPromptEntry[];
  stepStates: Record<string, StepState>;
  updateStep: (promptId: string, patch: Partial<StepState>) => void;
  runStep: (promptId: string) => void;
  availableModels: { key: string; value: string }[];
  onOverrideSaved: (promptId: string, override: import("../../features/admin/types").AdminPromptDbOverride) => void;
}) {
  const [selectedId, setSelectedId] = useState<string>(options[0].id);

  const prompt = prompts.find((p) => p.id === selectedId);
  const ss = stepStates[selectedId];

  if (!prompt || !ss) {
    return (
      <div className="rounded-xl border border-white/10 bg-gray-900/60 px-4 py-3 text-xs text-gray-500">
        Loading prompt registry…
      </div>
    );
  }

  const varNames = prompt.variables.map((v) => v.name);
  const resolvedSystem = fillVars(ss.systemOverride ?? prompt.system_prompt, varNames, ss.varValues);
  const resolvedUser = fillVars(ss.userOverride ?? prompt.user_prompt, varNames, ss.varValues);
  const hasDbOverride = !!prompt.db_override;

  return (
    <div className="rounded-xl border border-white/10 bg-gray-900/60 overflow-hidden">
      <div className="px-4 py-3 border-b border-white/8 flex items-center justify-between gap-3">
        <div>
          <div className="text-sm font-bold text-white">{title}</div>
          <div className="text-[10px] text-gray-500 mt-0.5">{subtitle}</div>
        </div>
        {hasDbOverride && (
          <div className="flex items-center gap-1.5 text-[10px] text-violet-300 bg-violet-900/20 border border-violet-700/30 rounded-lg px-2 py-1">
            <span className="w-1.5 h-1.5 rounded-full bg-violet-400 inline-block" />
            DB override active
          </div>
        )}
      </div>

      <div className="p-4 space-y-4">
        <div>
          <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">{selectorLabel}</label>
          <select
            value={selectedId}
            onChange={(e) => setSelectedId(e.target.value)}
            className="bg-black/30 border border-white/10 rounded-xl px-3 py-2 text-sm text-gray-200 focus:outline-none focus:border-indigo-500/50 w-full"
          >
            {options.map((o) => (
              <option key={o.id} value={o.id}>{o.label}</option>
            ))}
          </select>
          <p className="text-[10px] text-gray-600 mt-1 leading-relaxed">{prompt.description}</p>
        </div>

        {prompt.variables.length > 0 && (
          <div className="space-y-3">
            <div className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Variables — fill before running</div>
            {prompt.variables.map((v) => (
              <div key={v.name}>
                <label className="block text-[10px] text-gray-500 mb-1">
                  <code className="font-mono text-indigo-400">{"{" + v.name + "}"}</code>
                  {" — "}
                  <span className="text-gray-600">{v.description}</span>
                </label>
                <textarea
                  value={ss.varValues[v.name] ?? ""}
                  onChange={(e) => updateStep(prompt.id, { varValues: { ...ss.varValues, [v.name]: e.target.value } })}
                  rows={2}
                  placeholder={v.example ?? ""}
                  className="w-full bg-black/30 border border-white/10 rounded-xl px-3 py-2 text-xs text-gray-200 placeholder-gray-700 focus:outline-none focus:border-indigo-500/50 resize-none font-mono"
                />
              </div>
            ))}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <div>
            <div className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">System Prompt</div>
            <textarea
              className="w-full h-40 bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-xs text-gray-200 font-mono focus:outline-none focus:border-indigo-500/40 resize-y"
              value={ss.systemOverride ?? prompt.system_prompt}
              onChange={(e) => updateStep(prompt.id, { systemOverride: e.target.value })}
              placeholder="(no system prompt)"
            />
          </div>
          <div>
            <div className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">User Prompt</div>
            <textarea
              className="w-full h-40 bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-xs text-gray-200 font-mono focus:outline-none focus:border-indigo-500/40 resize-y"
              value={ss.userOverride ?? prompt.user_prompt}
              onChange={(e) => updateStep(prompt.id, { userOverride: e.target.value })}
            />
          </div>
        </div>

        <details className="group">
          <summary className="cursor-pointer text-[11px] text-gray-500 hover:text-gray-400 flex items-center gap-1.5 list-none">
            <span className="group-open:rotate-90 transition-transform inline-block text-gray-600">›</span>
            Resolved preview (with variables filled in)
          </summary>
          <div className="pt-2 space-y-2">
            {resolvedSystem && (
              <pre className="whitespace-pre-wrap text-[10px] text-gray-400 font-mono bg-black/20 rounded-lg px-3 py-2 max-h-32 overflow-y-auto">{resolvedSystem}</pre>
            )}
            <pre className="whitespace-pre-wrap text-[10px] text-gray-400 font-mono bg-black/20 rounded-lg px-3 py-2 max-h-32 overflow-y-auto">{resolvedUser}</pre>
          </div>
        </details>

        <div className="-mx-4">
          <PromptOverrideControls
            prompt={prompt}
            ss={ss}
            onUpdate={(patch) => updateStep(prompt.id, patch)}
            onOverrideSaved={onOverrideSaved}
            audienceLabel={audienceLabel}
          />
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 items-end">
          <div className="md:col-span-2">
            <label className="block text-[10px] text-gray-500 mb-1 font-semibold uppercase tracking-wider">Model</label>
            <ModelPicker
              value={ss.model}
              onChange={(v) => updateStep(prompt.id, { model: v })}
              resolvedModel={prompt.resolved_model}
              availableModels={availableModels}
            />
          </div>
          <div>
            <label className="block text-[10px] text-gray-500 mb-1 font-semibold uppercase tracking-wider">Temperature</label>
            <input
              type="number" step="0.05" min={0} max={2}
              className="w-full bg-black/30 border border-white/10 rounded-lg px-2.5 py-1.5 text-xs text-gray-200 focus:outline-none focus:border-indigo-500/50"
              value={ss.temperature}
              onChange={(e) => updateStep(prompt.id, { temperature: parseFloat(e.target.value) })}
            />
          </div>
          <div>
            <label className="block text-[10px] text-gray-500 mb-1 font-semibold uppercase tracking-wider">Max Tokens</label>
            <input
              type="number" min={1} max={32000}
              className="w-full bg-black/30 border border-white/10 rounded-lg px-2.5 py-1.5 text-xs text-gray-200 focus:outline-none focus:border-indigo-500/50"
              value={ss.maxTokens}
              onChange={(e) => updateStep(prompt.id, { maxTokens: parseInt(e.target.value, 10) || 1 })}
            />
            {(prompt.id === "rag_generator" || prompt.id === "rag_generator_seller") && (
              <ItineraryBoostControl onApply={(tokens) => updateStep(prompt.id, { maxTokens: tokens })} />
            )}
          </div>
        </div>

        <button
          onClick={() => runStep(prompt.id)}
          disabled={ss.running}
          title="Test this prompt against a real LLM call right now — does not save anything or affect live traffic"
          className="w-full bg-indigo-600 hover:bg-indigo-500 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-bold rounded-xl px-4 py-2.5 transition-colors"
        >
          {ss.running ? "Running…" : "▶ Test / Run"}
        </button>
        <p className="text-[10px] text-gray-600">Test/Run = one real LLM call, nothing saved. It does not affect live {audienceLabel} until you click Update Live above.</p>

        {ss.error && (
          <div className="text-xs text-red-400 bg-red-900/20 border border-red-700/30 rounded-xl px-3 py-2">{ss.error}</div>
        )}

        {ss.output !== null && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-semibold text-emerald-400 uppercase tracking-wider">Output</span>
              {ss.usage && (
                <span className="text-[10px] text-gray-600 font-mono">
                  {ss.usage.prompt_tokens}↑ · {ss.usage.completion_tokens}↓ · {ss.usage.total_tokens} tokens
                </span>
              )}
            </div>
            <pre className="bg-black/40 border border-white/8 rounded-xl p-4 text-xs text-gray-300 overflow-x-auto whitespace-pre-wrap leading-relaxed max-h-96 overflow-y-auto">
              {ss.output}
            </pre>
          </div>
        )}
      </div>
    </div>
  );
}

function RagChatPlayground({ overridePromptIds, prompts }: { overridePromptIds: string[]; prompts: AdminPromptEntry[] }) {
  const [messages, setMessages] = useState<RagChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [streaming, setStreaming] = useState(false);
  // Which prompt variant this test session uses: rag_router/rag_generator (buyer) vs
  // rag_router_seller/rag_generator_seller (seller). Also forces the same seller
  // competitor-isolation gate real sellers get (own-market deals/leads only).
  const [role, setRole] = useState<"buyer" | "seller">("buyer");
  const [sessions, setSessions] = useState<AdminRagSession[]>([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [loadingSession, setLoadingSession] = useState<string | null>(null);
  const [selectedDeal, setSelectedDeal] = useState<AdminDeal | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  const activeOverrides = overridePromptIds.filter(id => prompts.find(p => p.id === id)?.db_override);

  useEffect(() => { void loadSessions(); }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  async function loadSessions() {
    setSessionsLoading(true);
    try {
      const list = await adminService.listAdminRagSessions();
      setSessions(list);
    } catch { /* non-fatal */ }
    finally { setSessionsLoading(false); }
  }

  async function switchSession(sid: string) {
    if (sid === sessionId) return;
    setLoadingSession(sid);
    try {
      const doc = await adminService.getAdminRagSession(sid);
      const msgs: RagChatMessage[] = (doc.messages ?? []).map((m: Record<string, unknown>) => ({
        role: m.role as "user" | "assistant",
        text: m.text as string,
        deals: m.deals as AdminDeal[] | undefined,
        cross_sell_sections: m.cross_sell_sections as { category: string; label: string; deals: AdminDeal[] }[] | undefined,
        chips: m.chips as string[] | undefined,
        action: m.action as string | undefined,
        dmcs: m.dmcs as AdminDmcCard[] | undefined,
      }));
      setMessages(msgs);
      setSessionId(sid);
    } catch {
      setMessages([{ role: "assistant", text: "⚠️ Failed to load session. Please try again." }]);
    }
    finally { setLoadingSession(null); }
  }

  async function newSession() {
    setMessages([]);
    setSessionId(null);
  }

  async function sendMessage(overrideText?: string) {
    const text = (overrideText ?? input).trim();
    if (!text || streaming) return;
    setInput("");
    setMessages(prev => [...prev, { role: "user", text }]);
    const msgId = `ai-${Date.now()}`;
    setMessages(prev => [...prev, { role: "assistant", text: "", streaming: true }]);
    setStreaming(true);
    let metadataParsed: Record<string, unknown> = {};
    try {
      await adminService.streamAdminRagChat(
        text,
        sessionId,
        (sid) => setSessionId(sid),
        (token) => setMessages(prev => {
          const next = [...prev];
          const last = next[next.length - 1];
          if (last?.role === "assistant") next[next.length - 1] = { ...last, text: last.text + token };
          return next;
        }),
        (meta) => { metadataParsed = meta; },
        () => {
          // Apply metadata to last assistant message
          const rawDeals = (metadataParsed.deals as AdminDeal[]) ?? [];
          const rawCross = (metadataParsed.cross_sell ?? {}) as Record<string, AdminDeal[]>;
          const crossSections = Object.entries(rawCross)
            .filter(([, v]) => Array.isArray(v) && v.length > 0)
            .map(([cat, ds]) => ({ category: cat, label: cat.charAt(0).toUpperCase() + cat.slice(1), deals: ds }));
          const chips = (metadataParsed.chips as string[]) ?? [];
          const action = (metadataParsed.action as string) ?? "";
          const rawDmcs = (metadataParsed.dmcs as AdminDmcCard[]) ?? [];
          const finalText = (metadataParsed.generated_text as string) || undefined;
          setMessages(prev => {
            const next = [...prev];
            const lastIdx = next.length - 1;
            if (next[lastIdx]?.role === "assistant") {
              next[lastIdx] = {
                ...next[lastIdx],
                ...(finalText ? { text: finalText } : {}),
                streaming: false,
                deals: rawDeals.length > 0 ? rawDeals : undefined,
                cross_sell_sections: crossSections.length > 0 ? crossSections : undefined,
                chips: chips.length > 0 ? chips : undefined,
                action,
                dmcs: rawDmcs.length > 0 ? rawDmcs : undefined,
              };
            }
            return next;
          });
          void loadSessions();
        },
        role,
      );
    } catch (err) {
      const errMsg = err instanceof Error && err.message
        ? `⚠️ ${err.message}`
        : "⚠️ Something went wrong. Please try again.";
      setMessages(prev => {
        const next = prev.filter(m => !m.streaming);
        return [...next, { role: "assistant" as const, text: errMsg }];
      });
    } finally {
      setStreaming(false);
      setMessages(prev => prev.map(m => m.streaming ? { ...m, streaming: false } : m));
    }
    void msgId;
  }

  async function deleteSession(sid: string, e: React.MouseEvent) {
    e.stopPropagation();
    try { await adminService.clearAdminRagSession(sid); } catch { /* best effort */ }
    if (sid === sessionId) { setMessages([]); setSessionId(null); }
    void loadSessions();
  }

  return (
    <>
    {selectedDeal && <AdminDealDetailModal deal={selectedDeal} onClose={() => setSelectedDeal(null)} />}
    <div className="flex h-[calc(100vh-12rem)] min-h-[500px] rounded-xl border border-white/10 bg-gray-900/60 overflow-hidden">
      {/* Sessions sidebar */}
      <div className="w-56 shrink-0 border-r border-white/8 flex flex-col bg-black/20">
        <div className="px-3 py-2.5 border-b border-white/8 flex items-center justify-between">
          <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Sessions</span>
          <button onClick={newSession} title="New chat" className="text-[10px] text-indigo-400 hover:text-indigo-300 border border-indigo-700/30 rounded-md px-1.5 py-0.5 transition-colors">+ New</button>
        </div>
        <div className="flex-1 overflow-y-auto py-1">
          {sessionsLoading && <div className="text-[10px] text-gray-600 px-3 py-2"><Spinner /></div>}
          {!sessionsLoading && sessions.length === 0 && (
            <div className="text-[10px] text-gray-600 px-3 py-4 text-center">No saved sessions yet</div>
          )}
          {sessions.map(s => (
            <button
              key={s.session_id}
              onClick={() => void switchSession(s.session_id)}
              className={`w-full text-left px-3 py-2 group flex items-start gap-1.5 transition-colors ${
                s.session_id === sessionId
                  ? "bg-indigo-900/30 border-r-2 border-indigo-500"
                  : "hover:bg-white/4"
              }`}
            >
              {loadingSession === s.session_id
                ? <span className="text-[10px] text-gray-500 truncate flex-1"><Spinner /></span>
                : <>
                  <span className="flex-1 min-w-0">
                    <span className="block text-[10px] text-gray-300 truncate leading-snug">{s.preview || "(empty)"}</span>
                    <span className="block text-[9px] text-gray-600 mt-0.5">{s.message_count} msg{s.message_count !== 1 ? "s" : ""}</span>
                  </span>
                  <span
                    onClick={(e) => void deleteSession(s.session_id, e)}
                    className="opacity-0 group-hover:opacity-100 text-gray-600 hover:text-red-400 text-[10px] shrink-0 mt-0.5 transition-all cursor-pointer"
                    title="Delete session"
                  >✕</span>
                </>
              }
            </button>
          ))}
        </div>
        <div className="px-3 py-2 border-t border-white/8">
          <button onClick={loadSessions} className="text-[9px] text-gray-600 hover:text-gray-400 transition-colors">↻ Refresh</button>
        </div>
      </div>

      {/* Chat area */}
      <div className="flex flex-col flex-1 min-w-0">
        {/* Header */}
        <div className="px-4 py-3 border-b border-white/8 flex items-center justify-between gap-3 shrink-0">
          <div>
            <div className="text-sm font-bold text-white">RAG v2 Chat Playground</div>
            <div className="text-[10px] text-gray-500 mt-0.5">
              Full pipeline · admin session
              {sessionId && <span className="ml-2 font-mono text-gray-600">sid: {sessionId.slice(0, 12)}…</span>}
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <div className="flex items-center rounded-lg border border-white/10 overflow-hidden text-[10px] font-semibold">
              <button
                onClick={() => { setRole("buyer"); newSession(); }}
                title="Test as buyer — uses rag_router / rag_generator"
                className={`px-2.5 py-1.5 transition-colors ${role === "buyer" ? "bg-indigo-600/30 text-indigo-200" : "text-gray-500 hover:text-gray-300"}`}
              >
                Buyer
              </button>
              <button
                onClick={() => { setRole("seller"); newSession(); }}
                title="Test as seller — uses rag_router_seller / rag_generator_seller, same competitor-isolation gate real sellers get"
                className={`px-2.5 py-1.5 transition-colors border-l border-white/10 ${role === "seller" ? "bg-amber-600/30 text-amber-200" : "text-gray-500 hover:text-gray-300"}`}
              >
                Seller
              </button>
            </div>
            {activeOverrides.length > 0 && (
              <div className="flex items-center gap-1.5 text-[10px] text-violet-300 bg-violet-900/20 border border-violet-700/30 rounded-lg px-2 py-1">
                <span className="w-1.5 h-1.5 rounded-full bg-violet-400 inline-block" />
                {activeOverrides.length} DB override{activeOverrides.length > 1 ? "s" : ""} active
              </div>
            )}
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
          {messages.length === 0 && (
            <div className="flex flex-col items-center justify-center h-full gap-3 text-center">
              <div className="text-3xl">💬</div>
              <div className="text-sm text-gray-400 font-medium">Start chatting with the RAG v2 pipeline</div>
              <div className="text-[11px] text-gray-600 max-w-xs leading-relaxed">
                {activeOverrides.length > 0
                  ? `Using DB-saved overrides for: ${activeOverrides.join(", ")}.`
                  : "No DB overrides active — using code prompts."}
              </div>
              <div className="flex flex-wrap gap-2 mt-2 justify-center">
                {["Show me Bali packages", "Family trip under ₹1L", "Compare honeymoon deals"].map(s => (
                  <button key={s} onClick={() => void sendMessage(s)} className="text-[10px] px-3 py-1.5 rounded-full border border-indigo-700/40 text-indigo-300 hover:bg-indigo-900/20 transition-colors">
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}
          {messages.map((msg, i) => (
            <div key={i} className={`flex flex-col ${msg.role === "user" ? "items-end" : "items-start"}`}>
              {/* Bubble */}
              <div className={`max-w-[82%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${
                msg.role === "user"
                  ? "bg-indigo-600/30 border border-indigo-500/30 text-white rounded-br-sm"
                  : "bg-white/5 border border-white/8 text-gray-200 rounded-bl-sm"
              }`}>
                {msg.text || (msg.streaming ? <span className="inline-block w-1.5 h-4 bg-gray-400 animate-pulse rounded" /> : <span className="text-gray-600 text-xs italic">No response</span>)}
              </div>
              {/* Action badge */}
              {msg.role === "assistant" && msg.action && !msg.streaming && (
                <span className="mt-1 text-[9px] text-gray-600 border border-white/5 rounded px-1.5 py-0.5 bg-white/3">action: {msg.action}</span>
              )}
              {/* Deal cards */}
              {msg.role === "assistant" && msg.deals && msg.deals.length > 0 && !msg.streaming && (
                <div className="w-full max-w-[680px] mt-1">
                  <AdminDealsRail deals={msg.deals} onDealClick={setSelectedDeal} />
                </div>
              )}
              {/* Cross-sell sections */}
              {msg.role === "assistant" && msg.cross_sell_sections && msg.cross_sell_sections.length > 0 && !msg.streaming && (
                <div className="w-full max-w-[680px]">
                  {msg.cross_sell_sections.map(sec => (
                    <AdminDealsRail key={sec.category} deals={sec.deals} label={sec.label} onDealClick={setSelectedDeal} />
                  ))}
                </div>
              )}
              {/* DMC cards */}
              {msg.role === "assistant" && msg.dmcs && msg.dmcs.length > 0 && !msg.streaming && (
                <div className="w-full max-w-[680px] mt-2">
                  <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-1.5">{msg.dmcs.length} recommended DMC{msg.dmcs.length > 1 ? "s" : ""}</p>
                  <div className="flex gap-2 overflow-x-auto pb-1" style={{ scrollbarWidth: "none" }}>
                    {msg.dmcs.map(dmc => <AdminDmcCardUI key={dmc.dealer_id} dmc={dmc} />)}
                  </div>
                </div>
              )}
              {/* Follow-up chips */}
              {msg.role === "assistant" && msg.chips && msg.chips.length > 0 && !msg.streaming && (
                <div className="flex flex-wrap gap-1.5 mt-2 max-w-[680px]">
                  {msg.chips.map(chip => (
                    <button
                      key={chip}
                      onClick={() => void sendMessage(chip)}
                      disabled={streaming}
                      className="text-[10px] px-3 py-1.5 rounded-full border border-indigo-700/40 text-indigo-300 hover:bg-indigo-900/20 disabled:opacity-40 transition-colors"
                    >
                      {chip}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
          <div ref={bottomRef} />
        </div>

        {/* Input */}
        <div className="px-4 py-3 border-t border-white/8 shrink-0">
          <div className="flex gap-2">
            <input
              className="flex-1 bg-black/30 border border-white/10 rounded-xl px-4 py-2.5 text-sm text-gray-200 placeholder-gray-600 focus:outline-none focus:border-indigo-500/50 focus:ring-1 focus:ring-inset focus:ring-indigo-500/20"
              placeholder="Type a message…"
              value={input}
              disabled={streaming}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); void sendMessage(); } }}
            />
            <button
              onClick={() => void sendMessage()}
              disabled={streaming || !input.trim()}
              className="shrink-0 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-bold rounded-xl px-4 py-2.5 transition-colors"
            >
              {streaming ? "…" : "Send"}
            </button>
          </div>
        </div>
      </div>
    </div>
    </>
  );
}

// ── Market Research Chat Playground ─────────────────────────────────────────
// Isolated admin sandbox session — plain text Q&A only (Market Research has
// no deal cards / DMC cards / chips / buyer-seller prompt split, unlike RAG),
// so this is a smaller sibling of RagChatPlayground rather than a shared
// component. Every message here sends the Prompt Playground editor's CURRENT
// state (saved or not — same as Test/Run does for a single step) for
// market_research_answer / market_research_compress, so you can chat-test an
// unsaved edit before clicking Update Live. Save in the Prompt Playground tab
// still writes straight to system_prompts (immediately live for real users);
// this playground just lets you try a change safely first — no separate
// publish action needed either way.

type MarketResearchChatMessage = { role: "user" | "assistant"; text: string; streaming?: boolean; testedAgainst?: "draft" | "live" };

const MARKET_RESEARCH_DRAFT_PROMPT_IDS = [
  "market_research_extract_destination",
  "market_research_inventory_summary",
  "market_research_answer",
  "market_research_compress",
];

function MarketResearchChatPlayground({ prompts, stepStates }: { prompts: AdminPromptEntry[]; stepStates: Record<string, StepState> }) {
  const [messages, setMessages] = useState<MarketResearchChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [sessions, setSessions] = useState<AdminRagSession[]>([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [loadingSession, setLoadingSession] = useState<string | null>(null);
  // Explicit, visible choice — no silent behavior. "draft" = whatever's typed in the Prompt
  // Playground editor right now, saved or not. "live" = ignore the editor, use only what's
  // actually saved to system_prompts (same as a real buyer/seller would get right now).
  const [testMode, setTestMode] = useState<"draft" | "live">("draft");
  const bottomRef = useRef<HTMLDivElement>(null);

  const activeOverrides = MARKET_RESEARCH_DRAFT_PROMPT_IDS.filter(id => prompts.find(p => p.id === id)?.db_override);

  // Whatever's currently in the Prompt Playground editor for these prompts — same
  // "ss.systemOverride ?? prompt.system_prompt" fallback Test/Run and Update Live use, so
  // an unedited prompt still resolves to the right saved-or-default value, not a blank.
  function buildDraftOverrides() {
    const draft: Record<string, { system_prompt?: string; user_prompt?: string; model?: string | null; temperature?: number | null; max_tokens?: number | null }> = {};
    for (const id of MARKET_RESEARCH_DRAFT_PROMPT_IDS) {
      const p = prompts.find(pp => pp.id === id);
      const ss = stepStates[id];
      if (!p || !ss) continue;
      draft[id] = {
        system_prompt: ss.systemOverride ?? p.system_prompt,
        user_prompt: ss.userOverride ?? p.user_prompt,
        model: ss.model || null,
        temperature: ss.temperature,
        max_tokens: ss.maxTokens,
      };
    }
    return draft;
  }

  useEffect(() => { void loadSessions(); }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  async function loadSessions() {
    setSessionsLoading(true);
    try {
      const list = await adminService.listMarketResearchPlaygroundSessions();
      setSessions(list);
    } catch { /* non-fatal */ }
    finally { setSessionsLoading(false); }
  }

  async function switchSession(sid: string) {
    if (sid === sessionId) return;
    setLoadingSession(sid);
    try {
      const doc = await adminService.getMarketResearchPlaygroundSession(sid);
      const msgs: MarketResearchChatMessage[] = (doc.messages ?? []).map((m: Record<string, unknown>) => ({
        role: m.role as "user" | "assistant",
        text: m.text as string,
      }));
      setMessages(msgs);
      setSessionId(sid);
    } catch {
      setMessages([{ role: "assistant", text: "⚠️ Failed to load session. Please try again." }]);
    }
    finally { setLoadingSession(null); }
  }

  function newSession() {
    setMessages([]);
    setSessionId(null);
  }

  async function sendMessage(overrideText?: string) {
    const text = (overrideText ?? input).trim();
    if (!text || sending) return;
    const modeUsed = testMode;
    setInput("");
    setMessages(prev => [...prev, { role: "user", text }]);
    setMessages(prev => [...prev, { role: "assistant", text: "", streaming: true }]);
    setSending(true);
    try {
      const res = await adminService.runMarketResearchPlaygroundChat(
        text, sessionId, modeUsed === "draft" ? buildDraftOverrides() : undefined,
      );
      setSessionId(res.session_id);
      setMessages(prev => {
        const next = [...prev];
        const lastIdx = next.length - 1;
        if (next[lastIdx]?.role === "assistant") {
          next[lastIdx] = { role: "assistant", text: res.message, streaming: false, testedAgainst: modeUsed };
        }
        return next;
      });
      void loadSessions();
    } catch (err) {
      const errMsg = err instanceof Error && err.message ? `⚠️ ${err.message}` : "⚠️ Something went wrong. Please try again.";
      setMessages(prev => {
        const next = prev.filter(m => !m.streaming);
        return [...next, { role: "assistant" as const, text: errMsg }];
      });
    } finally {
      setSending(false);
    }
  }

  async function deleteSession(sid: string, e: React.MouseEvent) {
    e.stopPropagation();
    try { await adminService.clearMarketResearchPlaygroundSession(sid); } catch { /* best effort */ }
    if (sid === sessionId) { setMessages([]); setSessionId(null); }
    void loadSessions();
  }

  return (
    <div className="flex h-[calc(100vh-12rem)] min-h-[500px] rounded-xl border border-white/10 bg-gray-900/60 overflow-hidden">
      {/* Sessions sidebar */}
      <div className="w-56 shrink-0 border-r border-white/8 flex flex-col bg-black/20">
        <div className="px-3 py-2.5 border-b border-white/8 flex items-center justify-between">
          <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Sessions</span>
          <button onClick={newSession} title="New chat" className="text-[10px] text-indigo-400 hover:text-indigo-300 border border-indigo-700/30 rounded-md px-1.5 py-0.5 transition-colors">+ New</button>
        </div>
        <div className="flex-1 overflow-y-auto py-1">
          {sessionsLoading && <div className="text-[10px] text-gray-600 px-3 py-2"><Spinner /></div>}
          {!sessionsLoading && sessions.length === 0 && (
            <div className="text-[10px] text-gray-600 px-3 py-4 text-center">No saved sessions yet</div>
          )}
          {sessions.map(s => (
            <button
              key={s.session_id}
              onClick={() => void switchSession(s.session_id)}
              className={`w-full text-left px-3 py-2 group flex items-start gap-1.5 transition-colors ${
                s.session_id === sessionId
                  ? "bg-indigo-900/30 border-r-2 border-indigo-500"
                  : "hover:bg-white/4"
              }`}
            >
              {loadingSession === s.session_id
                ? <span className="text-[10px] text-gray-500 truncate flex-1"><Spinner /></span>
                : <>
                  <span className="flex-1 min-w-0">
                    <span className="block text-[10px] text-gray-300 truncate leading-snug">{s.preview || "(empty)"}</span>
                    <span className="block text-[9px] text-gray-600 mt-0.5">{s.message_count} msg{s.message_count !== 1 ? "s" : ""}</span>
                  </span>
                  <span
                    onClick={(e) => void deleteSession(s.session_id, e)}
                    className="opacity-0 group-hover:opacity-100 text-gray-600 hover:text-red-400 text-[10px] shrink-0 mt-0.5 transition-all cursor-pointer"
                    title="Delete session"
                  >✕</span>
                </>
              }
            </button>
          ))}
        </div>
        <div className="px-3 py-2 border-t border-white/8">
          <button onClick={loadSessions} className="text-[9px] text-gray-600 hover:text-gray-400 transition-colors">↻ Refresh</button>
        </div>
      </div>

      {/* Chat area */}
      <div className="flex flex-col flex-1 min-w-0">
        {/* Header */}
        <div className="px-4 py-3 border-b border-white/8 flex items-center justify-between gap-3 shrink-0">
          <div>
            <div className="text-sm font-bold text-white">Market Research Chat Playground</div>
            <div className="text-[10px] text-gray-500 mt-0.5">
              Isolated admin sandbox session
              {sessionId && <span className="ml-2 font-mono text-gray-600">sid: {sessionId.slice(0, 12)}…</span>}
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {/* The one button that decides what a message actually tests — always visible,
                always shows current state, nothing implicit. */}
            <div className="flex items-center rounded-lg border border-white/10 overflow-hidden text-[10px] font-semibold">
              <button
                onClick={() => setTestMode("draft")}
                title="Test using whatever's typed in the Prompt Playground editor right now — even if you haven't clicked Update Live yet"
                className={`px-2.5 py-1.5 transition-colors ${testMode === "draft" ? "bg-amber-600/30 text-amber-200" : "text-gray-500 hover:text-gray-300"}`}
              >
                🧪 Draft
              </button>
              <button
                onClick={() => setTestMode("live")}
                title="Ignore the editor — test only what's actually saved right now (what a real buyer/seller gets today)"
                className={`px-2.5 py-1.5 transition-colors border-l border-white/10 ${testMode === "live" ? "bg-emerald-600/30 text-emerald-200" : "text-gray-500 hover:text-gray-300"}`}
              >
                ✓ Live
              </button>
            </div>
            {activeOverrides.length > 0 && (
              <div className="flex items-center gap-1.5 text-[10px] text-violet-300 bg-violet-900/20 border border-violet-700/30 rounded-lg px-2 py-1">
                <span className="w-1.5 h-1.5 rounded-full bg-violet-400 inline-block" />
                {activeOverrides.length} saved override{activeOverrides.length > 1 ? "s" : ""}
              </div>
            )}
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
          {messages.length === 0 && (
            <div className="flex flex-col items-center justify-center h-full gap-3 text-center">
              <div className="text-3xl">📊</div>
              <div className="text-sm text-gray-400 font-medium">Ask a travel market research question</div>
              <div className="text-[11px] text-gray-600 max-w-xs leading-relaxed">
                {testMode === "draft"
                  ? "🧪 Draft mode — tests the Prompt Playground editor's current text, even if unsaved."
                  : "✓ Live mode — tests only what's actually saved to system_prompts right now."}
              </div>
              <div className="flex flex-wrap gap-2 mt-2 justify-center">
                {["What do Bali honeymoon packages cost right now?", "Competitive rates for Maldives overwater villas", "Best season pricing for Kerala backwaters"].map(s => (
                  <button key={s} onClick={() => void sendMessage(s)} className="text-[10px] px-3 py-1.5 rounded-full border border-indigo-700/40 text-indigo-300 hover:bg-indigo-900/20 transition-colors">
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}
          {messages.map((msg, i) => (
            <div key={i} className={`flex flex-col ${msg.role === "user" ? "items-end" : "items-start"}`}>
              <div className={`max-w-[82%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${
                msg.role === "user"
                  ? "bg-indigo-600/30 border border-indigo-500/30 text-white rounded-br-sm"
                  : "bg-white/5 border border-white/8 text-gray-200 rounded-bl-sm"
              }`}>
                {msg.text || (msg.streaming
                  ? <span className="flex items-center gap-1.5 text-xs text-gray-500 italic">
                      <span className="inline-block w-1.5 h-4 bg-gray-400 animate-pulse rounded" />
                      🔎 Researching — checking our inventory and live market data…
                    </span>
                  : <span className="text-gray-600 text-xs italic">No response</span>)}
              </div>
              {msg.role === "assistant" && msg.testedAgainst && !msg.streaming && (
                <span className={`mt-1 text-[9px] border rounded px-1.5 py-0.5 ${
                  msg.testedAgainst === "draft"
                    ? "text-amber-400 border-amber-700/30 bg-amber-900/10"
                    : "text-emerald-400 border-emerald-700/30 bg-emerald-900/10"
                }`}>
                  {msg.testedAgainst === "draft" ? "🧪 tested against draft editor" : "✓ tested against live/saved"}
                </span>
              )}
            </div>
          ))}
          <div ref={bottomRef} />
        </div>

        {/* Input */}
        <div className="px-4 py-3 border-t border-white/8 shrink-0">
          <div className="flex gap-2">
            <input
              className="flex-1 bg-black/30 border border-white/10 rounded-xl px-4 py-2.5 text-sm text-gray-200 placeholder-gray-600 focus:outline-none focus:border-indigo-500/50 focus:ring-1 focus:ring-inset focus:ring-indigo-500/20"
              placeholder="Type a message…"
              value={input}
              disabled={sending}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); void sendMessage(); } }}
            />
            <button
              onClick={() => void sendMessage()}
              disabled={sending || !input.trim()}
              className="shrink-0 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-bold rounded-xl px-4 py-2.5 transition-colors"
            >
              {sending ? "…" : "Send"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Step connector arrow ──────────────────────────────────────────────────────

function StepConnector() {
  return (
    <div className="flex justify-center my-0 py-1">
      <div className="flex flex-col items-center">
        <div className="w-px h-4 bg-white/10" />
        <div className="text-gray-700 text-xs leading-none">▼</div>
      </div>
    </div>
  );
}

// ── Info step (non-LLM) ───────────────────────────────────────────────────────

function InfoStepCard({ step, index }: { step: Extract<PipelineStep, { type: "info" }>; index: number }) {
  return (
    <div className="rounded-xl border border-blue-800/30 bg-blue-950/20 px-4 py-3 flex items-start gap-3">
      <div className="shrink-0 flex flex-col items-center gap-1 pt-0.5">
        <span className="w-6 h-6 rounded-full bg-blue-900/50 border border-blue-600/30 flex items-center justify-center text-[10px] font-bold text-blue-400">
          {index + 1}
        </span>
      </div>
      <div>
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-blue-300">{step.label}</span>
          {step.icon && <span className="text-base">{step.icon}</span>}
          <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-900/30 text-blue-500 border border-blue-700/30 font-semibold uppercase tracking-wider">System</span>
        </div>
        <p className="text-xs text-gray-400 mt-1 leading-relaxed">{step.note}</p>
      </div>
    </div>
  );
}

// ── Image-generation model-chain card ─────────────────────────────────────────
// For the 3 image-gen PROMPT_REGISTRY entries (flyer_studio_image_gen,
// itinerary_image_gen, deal_auto_flyer_image_gen). These have no system/user
// text — image models take a rendered prompt string, not a chat system role —
// and no temperature/max_tokens — image-gen APIs (FAL/Grok/Gemini image) take
// image_size/steps/guidance_scale instead, never a token budget. Only `model`
// is real here. Admin-set model is tried FIRST at the actual call site
// (fal_client.py / extraction_agent.py); the coded fallback chain always still
// runs unchanged behind it — this never disables or replaces the chain.
function ImageModelStepCard({
  step,
  index,
  prompt,
  ss,
  availableModels,
  onUpdate,
  onOverrideSaved,
  audienceLabel,
}: {
  step: Extract<PipelineStep, { type: "info" }>;
  index: number;
  prompt: AdminPromptEntry | undefined;
  ss: StepState | undefined;
  availableModels: { key: string; value: string }[];
  onUpdate: (patch: Partial<StepState>) => void;
  onOverrideSaved: (promptId: string, override: AdminPromptDbOverride) => void;
  audienceLabel: string;
}) {
  if (!prompt || !ss) {
    return <InfoStepCard step={step} index={index} />;
  }
  const hasDbOverride = !!prompt.db_override;

  return (
    <div className="rounded-xl border border-emerald-800/30 bg-emerald-950/10 overflow-hidden">
      <div className="px-4 py-3 flex items-start gap-3">
        <div className="shrink-0 flex flex-col items-center gap-1 pt-0.5">
          <span className="w-6 h-6 rounded-full bg-emerald-900/50 border border-emerald-600/30 flex items-center justify-center text-[10px] font-bold text-emerald-400">
            {index + 1}
          </span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-emerald-300">{step.label}</span>
            {step.icon && <span className="text-base">{step.icon}</span>}
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-900/30 text-emerald-500 border border-emerald-700/30 font-semibold uppercase tracking-wider">Image Model</span>
            {hasDbOverride && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-violet-900/30 text-violet-300 border border-violet-700/30 font-semibold uppercase tracking-wider flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-violet-400 inline-block" />
                DB override
              </span>
            )}
          </div>
          <p className="text-xs text-gray-400 mt-1 leading-relaxed">{step.note}</p>
          <p className="text-[10px] text-gray-600 mt-1.5">
            No temperature/max tokens here — image generation doesn't take a text-token budget the way chat completions do.
          </p>

          <div className="mt-3 max-w-md space-y-2">
            <label className="block text-[10px] text-gray-500 mb-1 font-semibold uppercase tracking-wider">
              Model chain — tried in order, top to bottom. Editing and saving this REPLACES the coded chain entirely (not a prepend); clear it back to empty to revert to the coded chain.
            </label>
            {ss.modelChain.map((m, i) => {
              const trimmed = m.trim();
              const isBadModel = trimmed.length > 0 && !isWorkingImageModel(trimmed);
              return (
                <div key={i}>
                  <div className="flex items-center gap-1.5">
                    <span className="shrink-0 w-5 text-[10px] text-gray-600 font-mono">{i + 1}.</span>
                    <div className="flex-1 min-w-0">
                      <ModelPicker
                        value={m}
                        onChange={(v) => {
                          const next = [...ss.modelChain];
                          next[i] = v;
                          onUpdate({ modelChain: next });
                        }}
                        resolvedModel={prompt.resolved_chain?.[i] ?? prompt.resolved_model}
                        availableModels={availableModels}
                        imageOnly
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        const next = ss.modelChain.filter((_, idx) => idx !== i);
                        onUpdate({ modelChain: next.length > 0 ? next : [""] });
                      }}
                      title="Remove this model from the chain"
                      className="shrink-0 text-gray-600 hover:text-red-400 text-sm leading-none px-1.5 py-1.5"
                    >
                      ×
                    </button>
                  </div>
                  {isBadModel && (
                    <p className="ml-6 mt-1 text-[10px] text-amber-400">
                      ⚠ Not a Grok or Gemini-image model — no working code path in fal_client.py for this id. It will fail (400, or return no image), not fall through as a working fallback.
                    </p>
                  )}
                </div>
              );
            })}
            <button
              type="button"
              onClick={() => onUpdate({ modelChain: [...ss.modelChain, ""] })}
              className="text-[10px] px-2.5 py-1.5 rounded-lg border border-dashed border-white/15 text-gray-400 hover:text-emerald-300 hover:border-emerald-600/40 transition-colors font-semibold"
            >
              + Add fallback model
            </button>
          </div>
        </div>
      </div>

      <PromptOverrideControls
        prompt={prompt}
        ss={ss}
        onUpdate={onUpdate}
        onOverrideSaved={onOverrideSaved}
        audienceLabel={audienceLabel}
        modelOnly
      />
    </div>
  );
}

// ── Prompt step card ──────────────────────────────────────────────────────────

export interface PromptStepCardProps {
  step: { type?: "prompt"; promptId: string; label: string; note?: string; optional?: boolean; roleGate?: "buyer" | "seller" };
  index: number;
  prompt: AdminPromptEntry | undefined;
  ss: StepState | undefined;
  availableModels: { key: string; value: string }[];
  onUpdate: (patch: Partial<StepState>) => void;
  onRun: () => void;
  onOverrideSaved: (promptId: string, override: AdminPromptDbOverride) => void;
}

export function PromptStepCard({ step, index, prompt, ss, availableModels, onUpdate, onRun, onOverrideSaved }: PromptStepCardProps) {
  if (!prompt || !ss) {
    return (
      <div className="rounded-xl border border-white/8 bg-white/3 px-4 py-3 text-xs text-gray-500">
        Prompt <code className="font-mono">{step.promptId}</code> not found in registry.
      </div>
    );
  }

  const varNames = prompt.variables.map((v) => v.name);
  const resolvedSystem = fillVars(ss.systemOverride ?? prompt.system_prompt, varNames, ss.varValues);
  const resolvedUser = fillVars(ss.userOverride ?? prompt.user_prompt, varNames, ss.varValues);
  const hasOutput = ss.output !== null;
  const isVision = prompt.media === "images" || prompt.media === "images (video frames)";
  const hasDbOverride = !!prompt.db_override;

  return (
    <div className={`rounded-xl border overflow-hidden ${
      hasOutput
        ? "border-emerald-700/30 bg-emerald-950/10"
        : ss.error
        ? "border-red-700/30 bg-red-950/10"
        : "border-white/8 bg-white/3"
    }`}>
      {/* Step header */}
      <div
        className="px-4 py-3 flex items-center gap-3 cursor-pointer hover:bg-white/3 transition-colors"
        onClick={() => onUpdate({ expanded: !ss.expanded })}
      >
        <div className="shrink-0 flex items-center gap-2">
          <span className="w-6 h-6 rounded-full bg-indigo-900/50 border border-indigo-500/30 flex items-center justify-center text-[10px] font-bold text-indigo-400">
            {index + 1}
          </span>
          {step.roleGate === "buyer" && (
            <span
              title="Runs for every buyer message — its seller counterpart runs instead when the user is a seller. Never both, never skipped."
              className="text-[9px] px-1.5 py-0.5 rounded bg-indigo-900/30 text-indigo-300 border border-indigo-700/40 font-semibold uppercase tracking-wider"
            >
              Buyer path
            </span>
          )}
          {step.roleGate === "seller" && (
            <span
              title="Runs for every seller message — its buyer counterpart runs instead when the user is a buyer. Never both, never skipped."
              className="text-[9px] px-1.5 py-0.5 rounded bg-amber-900/30 text-amber-300 border border-amber-700/40 font-semibold uppercase tracking-wider"
            >
              Seller path
            </span>
          )}
          {step.optional && !step.roleGate && (
            <span className="text-[9px] px-1.5 py-0.5 rounded bg-white/5 text-gray-500 border border-white/8 font-semibold uppercase tracking-wider">Optional</span>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-white leading-tight">{step.label}</div>
          <div className="text-[11px] text-gray-500 mt-0.5 truncate">{prompt.title}</div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {isVision && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-yellow-900/20 text-yellow-400 border border-yellow-700/30 font-semibold uppercase tracking-wider">Vision</span>
          )}
          {hasDbOverride && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-violet-900/30 text-violet-300 border border-violet-700/40 font-semibold uppercase tracking-wider">DB Override</span>
          )}
          {prompt.is_customized && (
            <span className="ml-2 text-[9px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 border border-amber-500/30">Customized</span>
          )}
          <span className="text-[10px] text-gray-500 font-mono">{modelLabel(prompt.default_model_setting)}</span>
          {hasOutput && <span className="text-emerald-400 text-xs">✓</span>}
          {ss.error && <span className="text-red-400 text-xs">✗</span>}
          <span className={`text-gray-500 text-xs transition-transform ${ss.expanded ? "rotate-90" : ""}`}>›</span>
        </div>
      </div>

      {/* Note */}
      {step.note && (
        <div className="px-4 pb-2 text-[11px] text-gray-500 leading-relaxed border-t border-white/5 pt-2">
          {step.note}
        </div>
      )}

      {/* Expanded: full editor */}
      {ss.expanded && (
        <div className="border-t border-white/8 divide-y divide-white/5">

          {/* Variables */}
          {prompt.variables.length > 0 && (
            <div className="px-4 py-3 space-y-3">
              <div className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Variables — fill before running</div>
              <div className="space-y-2">
                {prompt.variables.map((v) => (
                  <div key={v.name} className="grid grid-cols-5 gap-3 items-start">
                    <div className="col-span-2">
                      <div className="font-mono text-xs text-indigo-300">{`{${v.name}}`}</div>
                      <div className="text-[10px] text-gray-600 mt-0.5 leading-snug">{v.description}</div>
                    </div>
                    <textarea
                      className="col-span-3 bg-black/30 border border-white/10 rounded-lg px-2.5 py-1.5 text-xs text-gray-200 font-mono min-h-[38px] focus:outline-none focus:border-indigo-500/50 resize-y"
                      value={ss.varValues[v.name] ?? ""}
                      onChange={(e) => onUpdate({ varValues: { ...ss.varValues, [v.name]: e.target.value } })}
                      placeholder={v.example ?? ""}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Prompt editors */}
          <div className="px-4 py-3 grid grid-cols-1 lg:grid-cols-2 gap-3">
            <div>
              <div className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">System Prompt</div>
              <textarea
                className="w-full h-40 bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-xs text-gray-200 font-mono focus:outline-none focus:border-indigo-500/40 resize-y"
                value={ss.systemOverride ?? prompt.system_prompt}
                onChange={(e) => onUpdate({ systemOverride: e.target.value })}
                placeholder="(no system prompt)"
              />
            </div>
            <div>
              <div className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">User Prompt</div>
              <textarea
                className="w-full h-40 bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-xs text-gray-200 font-mono focus:outline-none focus:border-indigo-500/40 resize-y"
                value={ss.userOverride ?? prompt.user_prompt}
                onChange={(e) => onUpdate({ userOverride: e.target.value })}
              />
            </div>
          </div>

          {/* Resolved preview */}
          <details className="group">
            <summary className="px-4 py-2.5 cursor-pointer text-[11px] text-gray-500 hover:text-gray-400 flex items-center gap-1.5 list-none border-t border-white/5">
              <span className="group-open:rotate-90 transition-transform inline-block text-gray-600">›</span>
              Resolved preview (with variables filled in)
            </summary>
            <div className="px-4 pb-3 space-y-2">
              {resolvedSystem && (
                <pre className="whitespace-pre-wrap text-[10px] text-gray-400 font-mono bg-black/20 rounded-lg px-3 py-2 max-h-32 overflow-y-auto">{resolvedSystem}</pre>
              )}
              <pre className="whitespace-pre-wrap text-[10px] text-gray-400 font-mono bg-black/20 rounded-lg px-3 py-2 max-h-32 overflow-y-auto">{resolvedUser}</pre>
            </div>
          </details>

          <PromptOverrideControls
            prompt={prompt}
            ss={ss}
            onUpdate={onUpdate}
            onOverrideSaved={onOverrideSaved}
            audienceLabel="users"
          />

          {/* Model / settings row */}
          <div className="px-4 py-3 grid grid-cols-2 md:grid-cols-4 gap-3 items-end">
            <div className="md:col-span-2">
              <label className="block text-[10px] text-gray-500 mb-1 font-semibold uppercase tracking-wider">Model</label>
              <ModelPicker
                value={ss.model}
                onChange={(v) => onUpdate({ model: v })}
                resolvedModel={prompt.resolved_model}
                availableModels={availableModels}
              />
            </div>
            <div>
              <label className="block text-[10px] text-gray-500 mb-1 font-semibold uppercase tracking-wider">Temperature</label>
              <input
                type="number" step="0.05" min={0} max={2}
                className="w-full bg-black/30 border border-white/10 rounded-lg px-2.5 py-1.5 text-xs text-gray-200 focus:outline-none focus:border-indigo-500/50"
                value={ss.temperature}
                onChange={(e) => onUpdate({ temperature: parseFloat(e.target.value) })}
              />
            </div>
            <div>
              <label className="block text-[10px] text-gray-500 mb-1 font-semibold uppercase tracking-wider">Max Tokens</label>
              <input
                type="number" min={1} max={32000}
                className="w-full bg-black/30 border border-white/10 rounded-lg px-2.5 py-1.5 text-xs text-gray-200 focus:outline-none focus:border-indigo-500/50"
                value={ss.maxTokens}
                onChange={(e) => onUpdate({ maxTokens: parseInt(e.target.value, 10) || 1 })}
              />
              {(prompt.id === "rag_generator" || prompt.id === "rag_generator_seller") && (
                <ItineraryBoostControl onApply={(tokens) => onUpdate({ maxTokens: tokens })} />
              )}
            </div>
          </div>

          {/* Image URLs for vision prompts */}
          {isVision && (
            <div className="px-4 pb-3">
              <label className="block text-[10px] text-gray-500 mb-1 font-semibold uppercase tracking-wider">
                Image URLs <span className="normal-case font-normal text-gray-600">(one per line — required for this vision prompt)</span>
              </label>
              <textarea
                className="w-full h-14 bg-black/30 border border-white/10 rounded-lg px-2.5 py-1.5 text-xs text-gray-200 font-mono focus:outline-none focus:border-indigo-500/50 resize-none"
                value={ss.imageUrls}
                onChange={(e) => onUpdate({ imageUrls: e.target.value })}
                placeholder="https://example.com/image.jpg"
              />
            </div>
          )}

        </div>
      )}

      {/* Run button + output (always visible, even when collapsed) */}
      <div className={`px-4 py-3 ${ss.expanded ? "border-t border-white/8" : ""}`}>
        <button
          onClick={(e) => { e.stopPropagation(); onRun(); }}
          disabled={ss.running}
          title="Test this step against a real LLM call right now — does not save anything or affect live traffic"
          className="w-full bg-indigo-600 hover:bg-indigo-500 disabled:opacity-40 disabled:cursor-not-allowed text-white text-xs font-bold rounded-lg px-4 py-2 transition-colors"
        >
          {ss.running ? "Running…" : `▶  Test / Run Step ${index + 1}`}
        </button>
        <p className="text-[10px] text-gray-600 mt-1">Run = test only. It does not change what users get. Use Save to make a prompt live.</p>

        {ss.error && (
          <div className="mt-2 rounded-lg border border-red-700/40 bg-red-900/20 px-3 py-2 text-xs text-red-300">{ss.error}</div>
        )}

        {ss.output !== null && (
          <div className="mt-2">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] font-semibold text-emerald-400 uppercase tracking-wider">Output</span>
              <div className="flex items-center gap-3">
                {ss.usage && (
                  <span className="text-[10px] text-gray-600 font-mono">
                    {ss.usage.prompt_tokens}↑ · {ss.usage.completion_tokens}↓ · {ss.usage.total_tokens} tokens
                  </span>
                )}
                <button
                  onClick={() => navigator.clipboard.writeText(ss.output ?? "")}
                  className="text-[10px] text-gray-500 hover:text-gray-300 transition-colors"
                >
                  Copy
                </button>
              </div>
            </div>
            <pre className="whitespace-pre-wrap text-xs text-gray-200 font-mono bg-black/30 rounded-lg px-3 py-2.5 max-h-80 overflow-y-auto border border-emerald-700/20">
              {ss.output}
            </pre>
          </div>
        )}
      </div>
    </div>
  );
}

// ── PayloadUploader ───────────────────────────────────────────────────────────

interface PayloadUploaderProps {
  config: PipelineUploadConfig;
  onResult: (result: { file_type: string; text?: string; image_data_urls?: string[] }) => void;
}

function PayloadUploader({ config, onResult }: PayloadUploaderProps) {
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploaded, setUploaded] = useState<{ name: string; type: string; preview?: string } | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  async function handleFile(file: File) {
    setUploading(true);
    setUploadError(null);
    setUploaded(null);
    try {
      const result = await adminService.extractPromptFile(file);
      const preview = result.image_data_urls?.[0];
      setUploaded({ name: file.name, type: result.file_type, preview });
      onResult(result);
    } catch (err) {
      setUploadError(parseApiError(err).message);
    } finally {
      setUploading(false);
    }
  }

  function onDrop(e: React.DragEvent) {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  }

  return (
    <div className="rounded-xl border border-dashed border-amber-700/40 bg-amber-950/10 overflow-hidden">
      <div className="px-4 py-2.5 border-b border-amber-700/20 flex items-center gap-2">
        <span className="text-amber-400 text-sm">📎</span>
        <span className="text-xs font-semibold text-amber-300">{config.label}</span>
        <span className="text-[10px] text-gray-500 ml-1">{config.hint}</span>
      </div>

      <div
        className="px-4 py-4 flex items-center gap-4"
        onDragOver={(e) => e.preventDefault()}
        onDrop={onDrop}
      >
        {/* Drop zone / button */}
        <button
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="shrink-0 flex flex-col items-center justify-center gap-1.5 w-32 h-20 rounded-xl border border-dashed border-white/15 bg-white/3 hover:bg-white/6 hover:border-amber-600/40 transition-colors disabled:opacity-40 cursor-pointer"
        >
          {uploading ? (
            <span className="text-xs text-gray-400">Extracting…</span>
          ) : (
            <>
              <span className="text-xl">📂</span>
              <span className="text-[10px] text-gray-400 text-center leading-tight">Click or drag<br />file here</span>
            </>
          )}
        </button>
        <input
          ref={inputRef}
          type="file"
          accept={config.accepts}
          className="hidden"
          onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); e.target.value = ""; }}
        />

        {/* Status */}
        {uploaded && !uploadError && (
          <div className="flex items-center gap-3 flex-1 min-w-0">
            {uploaded.preview && (
              <img src={uploaded.preview} alt="" className="w-16 h-16 object-cover rounded-lg border border-white/10 shrink-0" />
            )}
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-emerald-400 text-sm">✓</span>
                <span className="text-xs font-semibold text-emerald-300">Extracted successfully</span>
                <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold uppercase tracking-wider ${
                  uploaded.type === "image" ? "bg-blue-900/30 text-blue-300 border border-blue-700/30" :
                  uploaded.type === "video" ? "bg-purple-900/30 text-purple-300 border border-purple-700/30" :
                  "bg-amber-900/30 text-amber-300 border border-amber-700/30"
                }`}>{uploaded.type}</span>
              </div>
              <div className="text-[11px] text-gray-500 mt-0.5 truncate">{uploaded.name}</div>
              <div className="text-[10px] text-emerald-600 mt-0.5">↳ Auto-filled relevant step variables — expand steps below to see</div>
            </div>
          </div>
        )}

        {uploadError && (
          <div className="flex-1 text-xs text-red-300 bg-red-900/20 rounded-lg px-3 py-2 border border-red-700/30">
            {uploadError}
          </div>
        )}

        {!uploaded && !uploadError && !uploading && (
          <div className="text-[11px] text-gray-600 leading-relaxed">
            Supported: {config.accepts.replace(/application\//g, "").replace(/\./g, "").replace(/,/g, " · ")}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Deal Analysis Playground ──────────────────────────────────────────────────

function DealAnalysisPlayground({ prompts }: { prompts: AdminPromptEntry[] }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [documentText, setDocumentText] = useState("");
  const [imageUrls, setImageUrls] = useState<string[]>([]);
  const [isVideo, setIsVideo] = useState(false);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<{ output: string; model: string; usage: Record<string, number>; prompt_used: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  const dealAnalysisPrompt = prompts.find((p) => p.id === "deal_analysis_extract");
  const hasDbOverride = !!dealAnalysisPrompt?.db_override;

  async function handleFileUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      const res = await adminService.extractPromptFile(file);
      if (res.file_type === "image" || res.file_type === "video") {
        setImageUrls(res.image_data_urls ?? []);
        setIsVideo(res.file_type === "video");
      } else if (res.file_type === "document" && res.text) {
        setDocumentText(res.text);
      }
    } catch (err) {
      setError(parseApiError(err).message);
    } finally {
      setUploading(false);
    }
    e.target.value = "";
  }

  async function handleRun() {
    setRunning(true);
    setError(null);
    setResult(null);
    try {
      const res = await adminService.runDealAnalysisPlayground({
        title,
        description,
        document_text: documentText || "(not provided)",
        image_urls: imageUrls,
        is_video: isVideo,
        model: null,
      });
      setResult(res);
    } catch (err) {
      setError(parseApiError(err).message);
    } finally {
      setRunning(false);
    }
  }

  return (
    <div className="rounded-xl border border-white/10 bg-gray-900/60 overflow-hidden">
      <div className="px-4 py-3 border-b border-white/8 flex items-center justify-between gap-3">
        <div>
          <div className="text-sm font-bold text-white">Deal Analysis Playground</div>
          <div className="text-[10px] text-gray-500 mt-0.5">End-to-end extraction · runs full deal_analysis_extract prompt</div>
        </div>
        {hasDbOverride && (
          <div className="flex items-center gap-1.5 text-[10px] text-violet-300 bg-violet-900/20 border border-violet-700/30 rounded-lg px-2 py-1">
            <span className="w-1.5 h-1.5 rounded-full bg-violet-400 inline-block" />
            DB override active
          </div>
        )}
      </div>

      <div className="p-4 space-y-4">
        <div>
          <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
            Upload deal asset (image / PDF / DOCX / video)
          </label>
          <div className="flex items-center gap-3">
            <label className={`cursor-pointer inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-white/10 bg-white/5 hover:bg-white/8 text-xs text-gray-300 transition-colors ${uploading ? "opacity-50 pointer-events-none" : ""}`}>
              {uploading ? "Extracting…" : "Choose file"}
              <input type="file" className="sr-only" accept="image/*,application/pdf,.pdf,.docx,.doc,video/*" onChange={handleFileUpload} />
            </label>
            {imageUrls.length > 0 && (
              <span className="text-[10px] text-emerald-400">{imageUrls.length} image{imageUrls.length > 1 ? "s" : ""} loaded</span>
            )}
            {documentText && (
              <span className="text-[10px] text-emerald-400">Document text loaded ({documentText.length.toLocaleString()} chars)</span>
            )}
          </div>
        </div>

        <div>
          <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Title</label>
          <textarea
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            rows={2}
            className="w-full bg-black/30 border border-white/10 rounded-xl px-3 py-2 text-sm text-gray-200 placeholder-gray-600 focus:outline-none focus:border-indigo-500/50 resize-none"
            placeholder="Seller-provided deal title…"
          />
        </div>

        <div>
          <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Description</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={4}
            className="w-full bg-black/30 border border-white/10 rounded-xl px-3 py-2 text-sm text-gray-200 placeholder-gray-600 focus:outline-none focus:border-indigo-500/50 resize-none"
            placeholder="Seller-provided deal description…"
          />
        </div>

        <label className="flex items-center gap-2.5 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={isVideo}
            onChange={(e) => setIsVideo(e.target.checked)}
            className="w-3.5 h-3.5 accent-indigo-500"
          />
          <span className="text-xs text-gray-300">
            Is video — appends the <code className="font-mono text-indigo-400">deal_analysis_video_addendum</code> prompt
            (DB-driven, same as the main extraction prompt — edit it in the registry below to change what gets appended)
          </span>
        </label>

        <button
          onClick={handleRun}
          disabled={running}
          className="w-full bg-indigo-600 hover:bg-indigo-500 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-bold rounded-xl px-4 py-2.5 transition-colors"
        >
          {running ? "Running Analysis…" : "Run Full Analysis"}
        </button>

        {error && (
          <div className="text-xs text-red-400 bg-red-900/20 border border-red-700/30 rounded-xl px-3 py-2">{error}</div>
        )}

        {result && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Output</span>
              <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold uppercase tracking-wider ${result.prompt_used === "db_override" ? "bg-violet-900/30 text-violet-300 border border-violet-700/30" : "bg-gray-800 text-gray-400 border border-white/10"}`}>
                {result.prompt_used === "db_override" ? "DB override" : "code prompt"}
              </span>
              <span className="text-[10px] text-gray-600 font-mono">{result.model}</span>
              {result.usage.total_tokens > 0 && (
                <span className="text-[10px] text-gray-600">{result.usage.total_tokens.toLocaleString()} tokens</span>
              )}
            </div>
            <pre className="bg-black/40 border border-white/8 rounded-xl p-4 text-xs text-gray-300 overflow-x-auto whitespace-pre-wrap leading-relaxed max-h-96 overflow-y-auto">
              {result.output}
            </pre>
          </div>
        )}
      </div>
    </div>
  );
}

// ── AI Media Generation Playground ───────────────────────────────────────────

// Video removed for now — see the removal note above the (deleted)
// ai_media_video_template pipeline entry. Re-add
// { value: "video_prompt_polish", label: "Video Prompt Polish" } (and its
// AI_MEDIA_PROMPT_IDS entry below) to bring it back.
const AI_MEDIA_GENERATION_TYPES = [
  { value: "context_extract", label: "Context Extract" },
  { value: "image_prompt_polish", label: "Image Prompt Polish" },
  { value: "template_parse", label: "Template Parse" },
] as const;

const AI_MEDIA_PROMPT_IDS: Record<string, string> = {
  image_prompt_polish: "ai_media_image_prompt_polish",
  context_extract: "ai_media_context_extract",
  template_parse: "ai_media_template_parse",
};

function AiMediaPlayground({ prompts }: { prompts: AdminPromptEntry[] }) {
  const [generationType, setGenerationType] = useState<string>("context_extract");
  const [userMessage, setUserMessage] = useState("");
  const [variables, setVariables] = useState<Record<string, string>>({});
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<{ output: string; model: string; usage: Record<string, number>; prompt_id: string; used_db_override: boolean } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const prevTypeRef = useRef(generationType);

  const promptId = AI_MEDIA_PROMPT_IDS[generationType];
  const activePrompt = prompts.find((p) => p.id === promptId);
  const hasDbOverride = !!activePrompt?.db_override;

  useEffect(() => {
    if (prevTypeRef.current !== generationType) {
      prevTypeRef.current = generationType;
      setVariables({});
      setResult(null);
      setError(null);
    }
  }, [generationType]);

  async function handleRun() {
    setRunning(true);
    setError(null);
    setResult(null);
    try {
      const res = await adminService.runAiMediaPlayground({
        generation_type: generationType,
        user_message: userMessage,
        variables,
        model: null,
      });
      setResult(res);
    } catch (err) {
      setError(parseApiError(err).message);
    } finally {
      setRunning(false);
    }
  }

  return (
    <div className="rounded-xl border border-white/10 bg-gray-900/60 overflow-hidden">
      <div className="px-4 py-3 border-b border-white/8 flex items-center justify-between gap-3">
        <div>
          <div className="text-sm font-bold text-white">AI Media Generation Playground</div>
          <div className="text-[10px] text-gray-500 mt-0.5">Test individual generation steps with variable overrides</div>
        </div>
        {hasDbOverride && (
          <div className="flex items-center gap-1.5 text-[10px] text-violet-300 bg-violet-900/20 border border-violet-700/30 rounded-lg px-2 py-1">
            <span className="w-1.5 h-1.5 rounded-full bg-violet-400 inline-block" />
            DB override active
          </div>
        )}
      </div>

      <div className="p-4 space-y-4">
        <div>
          <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Generation Type</label>
          <select
            value={generationType}
            onChange={(e) => setGenerationType(e.target.value)}
            className="bg-black/30 border border-white/10 rounded-xl px-3 py-2 text-sm text-gray-200 focus:outline-none focus:border-indigo-500/50 w-full"
          >
            {AI_MEDIA_GENERATION_TYPES.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
          {activePrompt && (
            <p className="text-[10px] text-gray-600 mt-1 leading-relaxed">{activePrompt.description}</p>
          )}
        </div>

        <div>
          <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">User Message / Input</label>
          <textarea
            value={userMessage}
            onChange={(e) => setUserMessage(e.target.value)}
            rows={3}
            className="w-full bg-black/30 border border-white/10 rounded-xl px-3 py-2 text-sm text-gray-200 placeholder-gray-600 focus:outline-none focus:border-indigo-500/50 resize-none"
            placeholder="Free-form user message or request…"
          />
        </div>

        {activePrompt && activePrompt.variables.length > 0 && (
          <div className="space-y-3">
            <div className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Template Variables</div>
            {activePrompt.variables.map((v) => (
              <div key={v.name}>
                <label className="block text-[10px] text-gray-500 mb-1">
                  <code className="font-mono text-indigo-400">{"{" + v.name + "}"}</code>
                  {" — "}
                  <span className="text-gray-600">{v.description}</span>
                </label>
                <textarea
                  value={variables[v.name] ?? ""}
                  onChange={(e) => setVariables((prev) => ({ ...prev, [v.name]: e.target.value }))}
                  rows={2}
                  placeholder={v.example ?? ""}
                  className="w-full bg-black/30 border border-white/10 rounded-xl px-3 py-2 text-xs text-gray-200 placeholder-gray-700 focus:outline-none focus:border-indigo-500/50 resize-none font-mono"
                />
              </div>
            ))}
          </div>
        )}

        <button
          onClick={handleRun}
          disabled={running}
          className="w-full bg-indigo-600 hover:bg-indigo-500 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-bold rounded-xl px-4 py-2.5 transition-colors"
        >
          {running ? "Running…" : "Run"}
        </button>

        {error && (
          <div className="text-xs text-red-400 bg-red-900/20 border border-red-700/30 rounded-xl px-3 py-2">{error}</div>
        )}

        {result && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Output</span>
              <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold uppercase tracking-wider ${result.used_db_override ? "bg-violet-900/30 text-violet-300 border border-violet-700/30" : "bg-gray-800 text-gray-400 border border-white/10"}`}>
                {result.used_db_override ? "DB override" : "code prompt"}
              </span>
              <span className="text-[10px] text-gray-600 font-mono">{result.model}</span>
              {result.usage.total_tokens > 0 && (
                <span className="text-[10px] text-gray-600">{result.usage.total_tokens.toLocaleString()} tokens</span>
              )}
            </div>
            <pre className="bg-black/40 border border-white/8 rounded-xl p-4 text-xs text-gray-300 overflow-x-auto whitespace-pre-wrap leading-relaxed max-h-96 overflow-y-auto">
              {result.output}
            </pre>
          </div>
        )}
      </div>
    </div>
  );
}
